import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sankey as createSankey, sankeyLinkHorizontal } from 'd3-sankey';
import type { SankeyLink, SankeyNode } from 'd3-sankey';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CommercialRepository, SdrGoalsRepository } from '../../data/repositories';
import {
  HabitaActiveFilterChip,
  HabitaButton,
  HabitaChartTooltipSurface,
  HabitaCheckbox,
  HabitaDateRangeFilter,
  HabitaDialog,
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaFloatingTooltip,
  HabitaPagination,
  HabitaRechartsTooltip,
  HabitaSectionHeader,
  HabitaSelect,
  classNames,
  formatHabitaDateInput,
  habitaSurfaceClass,
  resolveHabitaDateRange,
} from '../../shared/components/habita';
import type {
  CommercialChannelRankingItem,
  CommercialChannelsQualityResponse,
  CommercialDateBasis,
  CommercialFetchParams,
  CommercialFilterOption,
  CommercialFilterOptionsResponse,
  CommercialFunnelEventItem,
  CommercialFunnelEventKey,
  CommercialFunnelEventsResponse,
  CommercialFunnelBranchItem,
  CommercialFunnelOutcomeTone,
  CommercialFunnelResponse,
  CommercialFunnelStageItem,
  CommercialKpiKey,
  CommercialKpis,
  CommercialInterestRegionsResponse,
  CommercialOperationMetricsResponse,
  CommercialOperationStuckLeadItem,
  CommercialOperationStuckLeadStatus,
  CommercialOverviewResponse,
  CommercialSdrConversionItem,
  CommercialSdrConversionsResponse,
  CommercialSdrGoalItem,
  CommercialSdrGoalsResponse,
  CommercialSdrOpenWorkloadGroupKey,
  CommercialSdrOpenWorkloadItem,
  CommercialSdrOpenWorkloadResponse,
  CommercialSdrOutcomeTimeResponse,
  CommercialSdrRankingIndicator,
  CommercialSdrRankingResponse,
  CommercialSdrStageTimeItem,
  CommercialSdrStageTimeResponse,
  CommercialSankeyLinkItem,
  CommercialTeamGoalProgressResponse,
  CommercialTrendGranularity,
  GoalType,
  GoalsCompetencyConfiguration,
} from '../../shared/models';
import type { HabitaDateRangeFilterValue } from '../../shared/components/habita';
import { getAccessToken, getAuthProfiles } from '../../shared/utils/authStorage';
import {
  buildReferenceMonthOptions,
  currentReferenceMonth,
  formatMonthLabel,
} from '../../shared/utils/monthOptions';
import PerformanceRankingTab, {
  commercialRankingIndicatorCopy,
  commercialRankingIndicatorOptions,
} from './components/PerformanceRankingTab';
import type { PerformanceRankingTabHandle } from './components/PerformanceRankingTab';
import { calculateGoalPace, GoalPaceBadge, type GoalPace } from './components/GoalPaceBadge';
import TeamGoalGaugeCard from './components/TeamGoalGaugeCard';
import InterestRegionsCard from './components/InterestRegionsCard';

type CommercialTab = 'overview' | 'operation' | 'performance' | 'channels' | 'ranking';
type CommercialFilterKey =
  | 'channel'
  | 'channelDetail'
  | 'contactDdd'
  | 'cpfStatus'
  | 'interestCity'
  | 'opportunityStatus'
  | 'sdr'
  | 'stage';
type CommercialFilterState = Record<CommercialFilterKey, string[]>;

const operationMetricsWidgetKeys = [
  'operation-stage-time',
  'operation-loss-reasons',
  'operation-stuck-leads',
  'operation-approval-evolution',
  'operation-conversion-evolution',
  'operation-cycle-evolution',
] as const;

const operationWidgetKeys = [
  ...operationMetricsWidgetKeys,
  'operation-interest-regions',
] as const;

const performanceWidgetKeys = [
  'performance-goals',
  'performance-conversions',
  'performance-funnel-events',
  'performance-stage-time',
  'performance-open-workload',
  'performance-outcome-time',
] as const;

const channelWidgetKeys = [
  'channels-ranking',
  'channels-flow',
  'channels-cpf-quality',
] as const;

type OperationWidgetKey = typeof operationWidgetKeys[number];
type PerformanceWidgetKey = typeof performanceWidgetKeys[number];
type ChannelWidgetKey = typeof channelWidgetKeys[number];
type CommercialWidgetKey = OperationWidgetKey | PerformanceWidgetKey | ChannelWidgetKey;

const commercialWidgetKeys: CommercialWidgetKey[] = [
  ...operationWidgetKeys,
  ...performanceWidgetKeys,
  ...channelWidgetKeys,
];

interface CommercialWidgetDefinition {
  label: string;
  opportunityStatuses: string[];
}

interface CommercialWidgetFilterOverride {
  added: CommercialFilterState;
  removed: CommercialFilterState;
}

interface CommercialFilterGroup {
  collapsibleOptionGroup?: {
    group: string;
    label: string;
  };
  key: CommercialFilterKey;
  label: string;
  options: CommercialFilterOption[];
}

const COMMERCIAL_DATE_FILTER_STORAGE_KEY = 'habita4u.commercial.dateFilter';
const COMMERCIAL_DATE_BASIS_STORAGE_KEY = 'habita4u.commercial.dateBasis';

const commercialTabs: Array<{ key: CommercialTab; label: string }> = [
  { key: 'overview', label: 'Visão Geral' },
  { key: 'operation', label: 'Operação' },
  { key: 'performance', label: 'Performance' },
  { key: 'channels', label: 'Canais e Qualidade' },
  { key: 'ranking', label: 'Ranking SDRs' },
];

const commercialFunnelEvents: Array<{
  color: string;
  key: CommercialFunnelEventKey;
  label: string;
}> = [
  { color: '#d9a322', key: 'folder_generated', label: 'Pasta gerada' },
  { color: '#2f8f5b', key: 'approval', label: 'Aprovação' },
  { color: '#d94a3a', key: 'rejection', label: 'Reprovação' },
];

const sdrLineColors = ['#17462a', '#d9a322', '#2e7bb4', '#8f5da8', '#d94a3a', '#6b7280'];

const sdrOpenWorkloadGroups: Array<{
  color: string;
  key: CommercialSdrOpenWorkloadGroupKey;
  label: string;
}> = [
  { color: '#3b82f6', key: 'contact_attempt', label: 'Tentativa de contato' },
  { color: '#06b6d4', key: 'document_receipt', label: 'Recebimento de documentos' },
  { color: '#7c3aed', key: 'document_adjustment', label: 'Ajuste documental' },
  { color: '#22c55e', key: 'approved_or_conditional', label: 'Pasta aprovada ou aprovada com condicional' },
];

const opportunityStatusOptions: CommercialFilterOption[] = [
  { label: 'Ganho', value: 'won' },
  { label: 'Aberto', value: 'open' },
  { label: 'Perdido', value: 'lost' },
  { label: 'Abandonado', value: 'abandoned' },
];

const commercialFilterKeys: CommercialFilterKey[] = [
  'channel',
  'channelDetail',
  'contactDdd',
  'cpfStatus',
  'interestCity',
  'opportunityStatus',
  'sdr',
  'stage',
];

const commercialWidgetDefinitions: Record<CommercialWidgetKey, CommercialWidgetDefinition> = {
  'operation-stage-time': {
    label: 'Tempo médio de permanência por etapa',
    opportunityStatuses: ['open'],
  },
  'operation-loss-reasons': {
    label: 'Principais motivos de perda no funil',
    opportunityStatuses: ['lost'],
  },
  'operation-stuck-leads': {
    label: 'Leads parados',
    opportunityStatuses: ['open'],
  },
  'operation-approval-evolution': {
    label: 'Pastas aprovadas e visitas agendadas por mês',
    opportunityStatuses: ['open', 'won'],
  },
  'operation-conversion-evolution': {
    label: 'Evolução das taxas de conversão',
    opportunityStatuses: ['open', 'won'],
  },
  'operation-cycle-evolution': {
    label: 'Evolução do tempo médio de ciclo',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
  'operation-interest-regions': {
    label: 'Principais regiões de interesse',
    opportunityStatuses: ['open', 'won'],
  },
  'performance-goals': {
    label: 'Acompanhamento de metas',
    opportunityStatuses: ['open', 'won'],
  },
  'performance-conversions': {
    label: 'Conversão por SDR',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
  'performance-funnel-events': {
    label: 'Ritmo diário do funil',
    opportunityStatuses: ['open', 'won'],
  },
  'performance-stage-time': {
    label: 'Tempo médio por etapa e SDR',
    opportunityStatuses: ['open', 'won'],
  },
  'performance-open-workload': {
    label: 'Carteira ativa por SDR',
    opportunityStatuses: ['open', 'won'],
  },
  'performance-outcome-time': {
    label: 'Tempo até o desfecho',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
  'channels-ranking': {
    label: 'Ranking de canais por qualidade',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
  'channels-flow': {
    label: 'Fluxo - Canal → Detalhamento → Etapa final',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
  'channels-cpf-quality': {
    label: 'Canal x qualidade de CPF',
    opportunityStatuses: ['open', 'won', 'lost'],
  },
};

function createEmptyFilters(): CommercialFilterState {
  return {
    channel: [],
    channelDetail: [],
    contactDdd: [],
    cpfStatus: [],
    interestCity: [],
    opportunityStatus: [],
    sdr: [],
    stage: [],
  };
}

function createDefaultFilters(): CommercialFilterState {
  return {
    ...createEmptyFilters(),
    opportunityStatus: ['open', 'won'],
  };
}

function cloneFilters(filters: CommercialFilterState): CommercialFilterState {
  return commercialFilterKeys.reduce<CommercialFilterState>((clone, key) => {
    clone[key] = [...filters[key]];
    return clone;
  }, createEmptyFilters());
}

function areFiltersEqual(
  currentFilters: CommercialFilterState,
  nextFilters: CommercialFilterState
): boolean {
  return commercialFilterKeys.every((key) => (
    currentFilters[key].length === nextFilters[key].length
    && currentFilters[key].every((value) => nextFilters[key].includes(value))
  ));
}

function createWidgetDefaultFilters(widgetKey: CommercialWidgetKey): CommercialFilterState {
  return {
    ...createEmptyFilters(),
    opportunityStatus: [...commercialWidgetDefinitions[widgetKey].opportunityStatuses],
  };
}

function createEmptyWidgetFilterOverride(): CommercialWidgetFilterOverride {
  return {
    added: createEmptyFilters(),
    removed: createEmptyFilters(),
  };
}

function resolveWidgetFilters(
  widgetKey: CommercialWidgetKey,
  globalFilters: CommercialFilterState,
  override?: CommercialWidgetFilterOverride
): CommercialFilterState {
  const defaults = createWidgetDefaultFilters(widgetKey);

  return commercialFilterKeys.reduce<CommercialFilterState>((resolved, key) => {
    const values = new Set([
      ...defaults[key],
      ...globalFilters[key],
      ...(override?.added[key] ?? []),
    ]);
    (override?.removed[key] ?? []).forEach((value) => values.delete(value));
    resolved[key] = Array.from(values);
    return resolved;
  }, createEmptyFilters());
}

function hasWidgetFilterOverride(override?: CommercialWidgetFilterOverride): boolean {
  if (!override) return false;
  return commercialFilterKeys.some((key) => (
    override.added[key].length > 0 || override.removed[key].length > 0
  ));
}

const emptyOptions: CommercialFilterOptionsResponse = {
  channels: [],
  contact_ddds: [],
  cpf_statuses: [],
  details: [],
  interest_cities: [],
  ok: true,
  opportunity_statuses: opportunityStatusOptions,
  resource: 'filter-options',
  sdrs: [],
  stages: [],
};

function defaultDateFilter(): HabitaDateRangeFilterValue {
  return {
    customEnd: '',
    customStart: '',
    period: '30d',
  };
}

function readInitialDateFilter(): HabitaDateRangeFilterValue {
  if (typeof window === 'undefined') return defaultDateFilter();

  try {
    const stored = window.localStorage.getItem(COMMERCIAL_DATE_FILTER_STORAGE_KEY);
    if (!stored) return defaultDateFilter();
    const parsed = JSON.parse(stored) as Partial<HabitaDateRangeFilterValue>;
    if (!parsed.period) return defaultDateFilter();

    return {
      customEnd: typeof parsed.customEnd === 'string' ? parsed.customEnd : '',
      customStart: typeof parsed.customStart === 'string' ? parsed.customStart : '',
      period: parsed.period,
    } as HabitaDateRangeFilterValue;
  } catch {
    return defaultDateFilter();
  }
}

function persistDateFilter(value: HabitaDateRangeFilterValue) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(COMMERCIAL_DATE_FILTER_STORAGE_KEY, JSON.stringify(value));
}

function readInitialDateBasis(): CommercialDateBasis {
  if (typeof window === 'undefined') return 'creation';
  const stored = window.localStorage.getItem(COMMERCIAL_DATE_BASIS_STORAGE_KEY);
  return stored === 'status' || stored === 'stage' ? stored : 'creation';
}

function persistDateBasis(value: CommercialDateBasis) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(COMMERCIAL_DATE_BASIS_STORAGE_KEY, value);
}

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

function formatDurationDays(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}d`;
}

function formatDecimal(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return value.toLocaleString('pt-BR', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

const chartBarLabelProps = {
  fill: 'currentColor',
  fontSize: 10,
  fontWeight: 600,
  offset: 6,
} as const;

const maxComfortableInlineBarLabels = 16;

function shouldDisplayInlineBarLabels(categoryCount: number, seriesCount = 1): boolean {
  return categoryCount > 0
    && categoryCount * Math.max(seriesCount, 1) <= maxComfortableInlineBarLabels;
}

function formatCountBarLabel(value: unknown): string {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? formatNumber(numericValue)
    : '';
}

function formatPercentBarLabel(value: unknown): string {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? formatPercent(numericValue)
    : '';
}

function formatDaysBarLabel(value: unknown): string {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? formatDurationDays(numericValue)
    : '';
}

type CommercialSankeyNodeKind = 'channel' | 'detail' | 'outcome';

interface CommercialSankeyNodeDatum {
  color: string;
  id: string;
  kind: CommercialSankeyNodeKind;
  label: string;
}

interface CommercialSankeyLinkDatum {
  color: string;
}

type CommercialSankeyNode = SankeyNode<CommercialSankeyNodeDatum, CommercialSankeyLinkDatum>;
type CommercialSankeyLink = SankeyLink<CommercialSankeyNodeDatum, CommercialSankeyLinkDatum>;

const COMMERCIAL_SANKEY_WIDTH = 1000;
const COMMERCIAL_SANKEY_HEIGHT = 430;

const commercialSankeyChannelPalette = [
  '#2f6fbd',
  '#6f57d2',
  '#b84ca1',
  '#d15b6a',
  '#cf7b35',
  '#d9a322',
  '#2f8f5b',
  '#24879c',
  '#6b7280',
];

function commercialSankeyOutcomeColor(label: string): string {
  const normalized = label.toLowerCase();
  if (normalized.includes('condicional')) return '#d9a322';
  if (normalized.includes('aprovad')) return '#2f8f5b';
  if (normalized.includes('reprovad')) return '#d94a3a';
  if (normalized.includes('desqualificad')) return '#7a8376';
  if (normalized.includes('andamento')) return '#5aa7d6';
  return '#7a8376';
}

function isCommercialSankeyOutcomeLabel(label: string): boolean {
  const normalized = label.toLowerCase();
  return normalized.includes('condicional')
    || normalized.includes('aprovad')
    || normalized.includes('reprovad')
    || normalized.includes('desqualificad')
    || normalized.includes('andamento');
}

function firstAppearance(items: string[]): string[] {
  return Array.from(new Set(items.filter(Boolean)));
}

function buildCommercialSankeyGraph(rawLinks: CommercialSankeyLinkItem[], lowVolumeThreshold = 0.05) {
  const validLinks = rawLinks.filter((link) => link.source && link.target && Number.isFinite(link.value) && link.value > 0);
  const channelDetailLinks = validLinks.filter((link) => !isCommercialSankeyOutcomeLabel(link.target));
  const detailOutcomeLinks = validLinks.filter((link) => isCommercialSankeyOutcomeLabel(link.target));
  const rawChannelNames = firstAppearance(channelDetailLinks.map((link) => link.source));
  const outcomeNames = firstAppearance(detailOutcomeLinks.map((link) => link.target));
  const rawChannelTotals = new Map<string, number>();
  rawChannelNames.forEach((name) => rawChannelTotals.set(name, 0));
  channelDetailLinks.forEach((link) => {
    if (rawChannelTotals.has(link.source)) {
      rawChannelTotals.set(link.source, (rawChannelTotals.get(link.source) ?? 0) + link.value);
    }
  });

  const totalChannelVolume = Array.from(rawChannelTotals.values()).reduce((sum, value) => sum + value, 0);
  const minimumChannelVolume = totalChannelVolume * lowVolumeThreshold;
  const smallChannels = rawChannelNames.filter((name) => (rawChannelTotals.get(name) ?? 0) < minimumChannelVolume);
  const shouldGroupSmallChannels = smallChannels.length > 1;
  const groupedChannelSet = new Set(shouldGroupSmallChannels ? smallChannels : []);
  const channelNames = firstAppearance(rawChannelNames.map((name) => groupedChannelSet.has(name) ? 'Outros' : name));
  const detailNames = firstAppearance(
    [
      ...channelDetailLinks.map((link) => link.target),
      ...detailOutcomeLinks.map((link) => link.source),
    ],
  );

  const channelColor = new Map<string, string>();
  channelNames.forEach((name, index) => {
    channelColor.set(name, commercialSankeyChannelPalette[index % commercialSankeyChannelPalette.length]);
  });

  const nodes: CommercialSankeyNodeDatum[] = [];
  const nodeIndex = new Map<string, number>();
  const addNode = (label: string, kind: CommercialSankeyNodeKind, color: string) => {
    const id = `${kind}:${label}`;
    const existing = nodeIndex.get(id);
    if (existing !== undefined) return existing;
    const index = nodes.length;
    nodeIndex.set(id, index);
    nodes.push({ color, id, kind, label });
    return index;
  };

  channelNames.forEach((name) => addNode(name, 'channel', channelColor.get(name) ?? commercialSankeyChannelPalette[0]));
  detailNames.forEach((name) => addNode(name, 'detail', '#879184'));
  outcomeNames.forEach((name) => addNode(name, 'outcome', commercialSankeyOutcomeColor(name)));

  const linkMap = new Map<string, CommercialSankeyLinkDatum & { source: number; target: number; value: number }>();
  const detailDominantInbound = new Map<string, { color: string; value: number }>();

  validLinks.forEach((link) => {
    const isChannelDetailLink = !isCommercialSankeyOutcomeLabel(link.target);
    const sourceLabel = groupedChannelSet.has(link.source) ? 'Outros' : link.source;
    const sourceKind: CommercialSankeyNodeKind = isChannelDetailLink ? 'channel' : 'detail';
    const targetKind: CommercialSankeyNodeKind = isChannelDetailLink ? 'detail' : 'outcome';
    const sourceColor = sourceKind === 'channel' ? channelColor.get(sourceLabel) ?? commercialSankeyChannelPalette[0] : '#879184';
    const targetColor = targetKind === 'outcome' ? commercialSankeyOutcomeColor(link.target) : '#879184';
    const source = addNode(sourceLabel, sourceKind, sourceColor);
    const target = addNode(link.target, targetKind, targetColor);
    const key = `${source}->${target}`;
    const current = linkMap.get(key);

    if (current) {
      current.value += link.value;
    } else {
      linkMap.set(key, { color: sourceColor, source, target, value: link.value });
    }

    if (targetKind === 'detail') {
      const dominant = detailDominantInbound.get(link.target);
      if (!dominant || link.value > dominant.value) {
        detailDominantInbound.set(link.target, { color: sourceColor, value: link.value });
      }
    }
  });

  const graphLinks = Array.from(linkMap.values()).map((link) => {
    const sourceNode = nodes[link.source];
    const color = sourceNode.kind === 'detail'
      ? detailDominantInbound.get(sourceNode.label)?.color ?? sourceNode.color
      : link.color;
    return { ...link, color };
  });

  const layout = createSankey<CommercialSankeyNodeDatum, CommercialSankeyLinkDatum>()
    .nodeWidth(12)
    .nodePadding(10)
    .nodeSort(null)
    .extent([[6, 10], [COMMERCIAL_SANKEY_WIDTH - 6, COMMERCIAL_SANKEY_HEIGHT - 10]]);

  const graph = layout({
    links: graphLinks.map((link) => ({ ...link })),
    nodes: nodes.map((node) => ({ ...node })),
  });

  return {
    links: graph.links as CommercialSankeyLink[],
    nodes: graph.nodes as CommercialSankeyNode[],
  };
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '-';
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}

const shortMonthLabels = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function parseTrendDate(value: string): Date | null {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTrendAxisLabel(value: string, granularity: CommercialTrendGranularity): string {
  const date = parseTrendDate(value);
  if (!date) return value;
  if (granularity === 'monthly') {
    return `${shortMonthLabels[date.getMonth()]}/${String(date.getFullYear()).slice(-2)}`;
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatTrendPeriodLabel(value: string, granularity: CommercialTrendGranularity): string {
  const start = parseTrendDate(value);
  if (!start) return value;
  if (granularity === 'monthly') {
    const label = start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (granularity === 'weekly') {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`;
  }
  return start.toLocaleDateString('pt-BR');
}

function createdInPeriodLabel(granularity: CommercialTrendGranularity): string {
  if (granularity === 'monthly') return 'Lead criado no mês';
  if (granularity === 'weekly') return 'Lead criado na semana';
  return 'Lead criado no dia';
}

function formatSignedPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Sem base anterior';
  return `${value >= 0 ? '+' : ''}${value.toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })}% vs período anterior`;
}

function comparisonTone(
  value: number | null,
  improvement: 'decrease' | 'increase'
): 'negative' | 'neutral' | 'positive' {
  if (value === null || !Number.isFinite(value) || Math.abs(value) < 0.005) return 'neutral';
  return improvement === 'increase'
    ? value > 0 ? 'positive' : 'negative'
    : value < 0 ? 'positive' : 'negative';
}

function joined(values: string[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

function buildCommercialQueryParams(
  filters: CommercialFilterState,
  dateBasis: CommercialDateBasis,
  startDate: string | undefined,
  endDate: string | undefined
): CommercialFetchParams {
  const opportunityStatuses = filters.opportunityStatus.length > 0
    ? filters.opportunityStatus
    : opportunityStatusOptions.map((option) => option.value);

  return {
    channel: joined(filters.channel),
    channel_detail: joined(filters.channelDetail),
    contact_ddd: joined(filters.contactDdd),
    cpf_status: joined(filters.cpfStatus),
    date_basis: dateBasis,
    endDate,
    interest_city: joined(filters.interestCity),
    limit: 5,
    opportunity_status: joined(opportunityStatuses),
    sdr: joined(filters.sdr),
    stage: joined(filters.stage),
    startDate,
  };
}

async function fetchDeduplicatedWidgetData<TKey extends string, TData>(
  widgetKeys: readonly TKey[],
  paramsByWidget: Record<TKey, CommercialFetchParams>,
  fetcher: (params: CommercialFetchParams) => Promise<TData>
): Promise<{
  errors: unknown[];
  failedWidgetKeys: TKey[];
  responses: Record<TKey, TData | null>;
}> {
  type RequestResult = {
    data: TData | null;
    error: unknown | null;
  };

  const requestsBySignature = new Map<string, Promise<RequestResult>>();
  const entries = await Promise.all(widgetKeys.map(async (widgetKey) => {
    const params = paramsByWidget[widgetKey];
    const signature = JSON.stringify(params);
    let request = requestsBySignature.get(signature);

    if (!request) {
      request = fetcher(params)
        .then((data) => ({ data, error: null }))
        .catch((error: unknown) => ({ data: null, error }));
      requestsBySignature.set(signature, request);
    }

    return [widgetKey, await request] as const;
  }));

  const failedWidgetKeys: TKey[] = [];
  const errors = new Set<unknown>();
  const responses = Object.fromEntries(entries.map(([widgetKey, result]) => {
    if (result.error !== null) {
      failedWidgetKeys.push(widgetKey);
      errors.add(result.error);
    }
    return [widgetKey, result.data];
  })) as Record<TKey, TData | null>;

  return {
    errors: Array.from(errors),
    failedWidgetKeys,
    responses,
  };
}

function activeFilterItems(filters: CommercialFilterState, groups: CommercialFilterGroup[]) {
  const groupByKey = new Map(groups.map((group) => [group.key, group]));

  return (Object.keys(filters) as CommercialFilterKey[]).flatMap((key) => {
    const group = groupByKey.get(key);
    return filters[key].map((value) => ({
      displayValue: group?.options.find((option) => option.value === value)?.label ?? value,
      key,
      label: group?.label ?? key,
      value,
    }));
  });
}

function CommercialFilterGroupCard({
  group,
  onToggle,
  renderOptionMeta,
  selectedValues,
}: {
  group: CommercialFilterGroup;
  onToggle: (value: string) => void;
  renderOptionMeta?: (option: CommercialFilterOption) => ReactNode;
  selectedValues: string[];
}) {
  const collapsibleGroup = group.collapsibleOptionGroup;
  const visibleOptions = collapsibleGroup
    ? group.options.filter((option) => option.group !== collapsibleGroup.group)
    : group.options;
  const collapsibleOptions = collapsibleGroup
    ? group.options.filter((option) => option.group === collapsibleGroup.group)
    : [];
  const selectedCollapsibleCount = collapsibleOptions.filter((option) => (
    selectedValues.includes(option.value)
  )).length;
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(selectedCollapsibleCount > 0);

  const renderOption = (option: CommercialFilterOption) => (
    <HabitaCheckbox
      key={`${group.key}-${option.value}`}
      checked={selectedValues.includes(option.value)}
      onCheckedChange={() => onToggle(option.value)}
    >
      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <span className="truncate font-instrument text-sm text-textPrimary">{option.label}</span>
        {renderOptionMeta?.(option)}
      </span>
    </HabitaCheckbox>
  );

  return (
    <section className="rounded-lg border border-border-subtle">
      <div className="border-b border-border-subtle px-3 py-2">
        <h3 className="font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
          {group.label}
        </h3>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {group.options.length > 0 ? (
          <>
            {visibleOptions.map(renderOption)}
            {collapsibleGroup && collapsibleOptions.length > 0 && (
              <>
                <button
                  aria-expanded={isCollapsibleOpen}
                  className="flex w-full items-center justify-between border-t border-border-subtle px-3 py-2 text-left font-instrument text-sm font-semibold text-textPrimary transition-colors hover:bg-muted/50"
                  onClick={() => setIsCollapsibleOpen((current) => !current)}
                  type="button"
                >
                  <span>{collapsibleGroup.label}</span>
                  <span className="flex items-center gap-2 text-xs text-textSecondary">
                    {selectedCollapsibleCount > 0 && `${selectedCollapsibleCount} selecionado(s)`}
                    <i
                      aria-hidden
                      className={classNames(
                        'ri-arrow-down-s-line text-base transition-transform',
                        isCollapsibleOpen && 'rotate-180'
                      )}
                    />
                  </span>
                </button>
                {isCollapsibleOpen && (
                  <div className="border-t border-border-subtle bg-muted/20 py-1">
                    {collapsibleOptions.map(renderOption)}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <p className="px-3 py-4 font-instrument text-sm text-textSecondary">
            Sem opções disponíveis.
          </p>
        )}
      </div>
    </section>
  );
}

function CommercialWidgetFilterButton({
  filterCount,
  isCustomized,
  onClick,
}: {
  filterCount: number;
  isCustomized: boolean;
  onClick: () => void;
}) {
  return (
    <HabitaButton
      aria-label="Configurar filtros deste componente"
      icon="ri-filter-3-line"
      onClick={onClick}
      size="sm"
      title="Configurar filtros deste componente"
      variant={isCustomized ? 'primary' : 'secondary'}
    >
      Filtros
      <span
        className={classNames(
          'rounded-full px-1.5 text-[11px]',
          isCustomized ? 'bg-black/10 dark:bg-white/20' : 'bg-muted text-textSecondary'
        )}
      >
        {filterCount}
      </span>
    </HabitaButton>
  );
}

function CommercialWidgetFilterSummary({
  filters,
  groups,
  onRemove,
}: {
  filters: CommercialFilterState;
  groups: CommercialFilterGroup[];
  onRemove: (key: CommercialFilterKey, value: string) => void;
}) {
  const items = activeFilterItems(filters, groups);
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-muted/20 px-4 py-2">
      <span className="inline-flex items-center gap-1 font-instrument text-[11px] font-semibold uppercase text-textSecondary">
        <i className="ri-filter-3-line text-xs" aria-hidden />
        Filtros aplicados
      </span>
      {items.map((filter) => (
        <HabitaActiveFilterChip
          key={`${filter.key}-${filter.value}`}
          aria-label={`Remover filtro ${filter.label} apenas deste componente`}
          label={filter.label}
          onRemove={() => onRemove(filter.key, filter.value)}
          value={filter.displayValue}
        />
      ))}
    </div>
  );
}

function CommercialTabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        'h-9 shrink-0 rounded-md px-3 font-instrument text-sm font-medium transition-colors',
        active
          ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
          : 'text-textSecondary hover:bg-accent hover:text-textPrimary'
      )}
    >
      {children}
    </button>
  );
}

function CommercialTabLoadingState({ title = 'Carregando dados' }: { title?: string }) {
  return (
    <section className={classNames(habitaSurfaceClass, 'p-6')}>
      <div className="h-5 w-48 animate-pulse rounded bg-muted" />
      <p className="mt-3 font-instrument text-sm text-textSecondary">{title}</p>
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-56 animate-pulse rounded-lg bg-muted/70" />
        ))}
      </div>
    </section>
  );
}

function CommercialKpiCard({
  aux,
  goalPace,
  hint,
  icon,
  improvement = 'increase',
  label,
  metricKey,
  value,
  variations,
}: {
  aux?: string;
  goalPace?: GoalPace | null;
  hint: string;
  icon: string;
  improvement?: 'decrease' | 'increase';
  label: string;
  metricKey: CommercialKpiKey;
  value: ReactNode;
  variations: CommercialKpis['variations'];
}) {
  const variation = variations[metricKey];
  const tone = comparisonTone(variation, improvement);

  return (
    <article
      className={classNames(
        habitaSurfaceClass,
        'flex min-h-[150px] flex-col justify-center border-l-4 border-l-primary/40 p-4'
      )}
      title={hint}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 font-instrument text-[11px] font-semibold uppercase leading-snug tracking-wide text-textSecondary">
          {label}
        </p>
        <i className={classNames(icon, 'shrink-0 text-base text-textSecondary')} aria-hidden />
      </div>
      <p className="mt-3 font-instrument text-2xl font-semibold leading-tight text-textPrimary">{value}</p>
      {aux && <p className="mt-1 font-instrument text-xs text-textSecondary">{aux}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <p
          className={classNames(
            'font-instrument text-xs font-medium',
            tone === 'positive' && 'text-emerald-700 dark:text-emerald-300',
            tone === 'negative' && 'text-red-700 dark:text-red-300',
            tone === 'neutral' && 'text-textSecondary'
          )}
        >
          {formatSignedPercent(variation)}
        </p>
        {goalPace && <GoalPaceBadge compact pace={goalPace} />}
      </div>
    </article>
  );
}

function RankingRow({
  index,
  item,
  metricLabel,
  metricValue,
}: {
  index: number;
  item: CommercialChannelRankingItem;
  metricLabel: string;
  metricValue: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-accent/70">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted font-instrument text-xs font-semibold text-textPrimary">
          {item.rank ?? index + 1}
        </span>
        <div className="min-w-0">
          <p className="truncate font-instrument text-sm font-semibold text-textPrimary">{item.channel}</p>
          <p className="truncate font-instrument text-xs text-textSecondary">
            {item.detail} · {metricLabel}
          </p>
        </div>
      </div>
      <span className="shrink-0 text-right font-instrument text-sm font-semibold text-textPrimary">{metricValue}</span>
    </div>
  );
}

function RankingCard({
  items,
  metric,
  onDetails,
  relevantTotal,
  title,
}: {
  items: CommercialChannelRankingItem[];
  metric: 'approval' | 'mql' | 'volume';
  onDetails: () => void;
  relevantTotal: number;
  title: string;
}) {
  const shareOfRelevantTotal = (value: number) => (
    relevantTotal > 0 ? (value / relevantTotal) * 100 : null
  );

  function metricText(item: CommercialChannelRankingItem): { label: string; value: string } {
    if (metric === 'approval') {
      return {
        label: `${formatNumber(item.approved_leads)} aprovados · ${formatPercent(shareOfRelevantTotal(item.approved_leads))} dos aprovados`,
        value: formatPercent(item.approval_rate_pct),
      };
    }

    if (metric === 'mql') {
      return {
        label: `${formatNumber(item.mql_leads)} MQL · ${formatPercent(shareOfRelevantTotal(item.mql_leads))} dos MQL`,
        value: `${formatNumber(item.mql_leads)} MQL`,
      };
    }

    return {
      label: `${formatNumber(item.total_leads)} leads · ${formatPercent(item.share_of_total_pct)} do total`,
      value: formatNumber(item.total_leads),
    };
  }

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title={title} className="px-4 py-3">
        <button
          type="button"
          onClick={onDetails}
          className="inline-flex items-center gap-1 font-instrument text-xs font-medium text-primary transition-colors hover:text-primary/80"
        >
          ver detalhes
          <i className="ri-arrow-right-s-line text-base" aria-hidden />
        </button>
      </HabitaSectionHeader>
      <div className="space-y-1 p-3">
        {items.length > 0 ? (
          items.map((item, index) => {
            const text = metricText(item);
            return (
              <RankingRow
                key={`${item.channel}-${item.detail}-${index}`}
                index={index}
                item={item}
                metricLabel={text.label}
                metricValue={text.value}
              />
            );
          })
        ) : (
          <div className="px-2 py-8 text-center font-instrument text-sm text-textSecondary">
            Sem canais para o período selecionado.
          </div>
        )}
      </div>
    </section>
  );
}

function OverviewTab({
  data,
  funnel,
  funnelLoading,
  goalConfiguration,
  goalProgress,
  goalTrackingError,
  goalTrackingLoading,
  onDetails,
}: {
  data: CommercialOverviewResponse | null;
  funnel: CommercialFunnelResponse | null;
  funnelLoading: boolean;
  goalConfiguration: GoalsCompetencyConfiguration | null;
  goalProgress: CommercialTeamGoalProgressResponse | null;
  goalTrackingError: string;
  goalTrackingLoading: boolean;
  onDetails: () => void;
}) {
  const kpis = data?.kpis.current;
  const variations = data?.kpis.variations ?? {} as CommercialKpis['variations'];
  const generatedFromCleanRate = kpis && kpis.mql_leads > 0
    ? (kpis.generated_folders / kpis.mql_leads) * 100
    : null;
  const approvedFromGeneratedRate = kpis && kpis.generated_folders > 0
    ? (kpis.sql_leads / kpis.generated_folders) * 100
    : null;
  const paceFor = (type: GoalType): GoalPace | null => {
    const goalType = goalConfiguration?.goal_types.find((item) => item.type === type);
    const target = goalConfiguration?.team_goals.find((item) => item.type === type)?.goal;
    if (!goalType?.enabled || !target || !goalProgress) return null;

    return calculateGoalPace({
      actual: goalProgress.actuals[type],
      referenceMonth: goalConfiguration.competency.period,
      target,
      totalWorkingDays: goalConfiguration.competency.working_days,
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-8">
        <CommercialKpiCard
          hint="Quantidade de leads que entraram no Funil SDR no período selecionado."
          icon="ri-group-line"
          label="Novos leads"
          metricKey="total_leads"
          value={formatNumber(kpis?.total_leads ?? 0)}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatPercent(kpis?.cpf_collected_rate_pct ?? null)} dos novos leads`}
          hint="Quantidade de novos leads cujo CPF já possui resultado limpo ou sujo."
          icon="ri-id-card-line"
          label="CPF coletado"
          metricKey="cpf_collected_leads"
          value={formatNumber(kpis?.cpf_collected_leads ?? 0)}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatPercent(kpis?.cpf_clean_of_collected_rate_pct ?? null)} limpo do CPF coletado`}
          hint="Distribuição dos CPFs coletados entre resultado limpo e resultado sujo."
          icon="ri-shield-check-line"
          label="CPF validado"
          metricKey="cpf_collected_leads"
          value={`${formatNumber(kpis?.mql_leads ?? 0)} limpo · ${formatNumber(kpis?.cpf_dirty_leads ?? 0)} sujo`}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatPercent(generatedFromCleanRate)} dos CPF limpos`}
          goalPace={paceFor('PASTAS_GERADAS')}
          hint="Quantidade de oportunidades que passaram por Aprovação de Crédito - CCA, Pasta Aprovada, Pasta Reprovada, Pasta Aprovada com Condicional ou Agenda Marcada."
          icon="ri-folder-check-line"
          label="Pastas geradas"
          metricKey="generated_folders"
          value={formatNumber(kpis?.generated_folders ?? 0)}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatPercent(approvedFromGeneratedRate)} das pastas geradas`}
          goalPace={paceFor('PASTAS_APROVADAS')}
          hint="Quantidade de pastas com desfecho Aprovado ou Aprovado com Condicional."
          icon="ri-checkbox-circle-line"
          label="Pastas aprovadas"
          metricKey="sql_leads"
          value={formatNumber(kpis?.sql_leads ?? 0)}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatPercent(kpis?.scheduled_from_approved_rate_pct ?? null)} das pastas aprovadas + condicional`}
          goalPace={paceFor('VISITAS_AGENDADAS')}
          hint="Quantidade de leads que alcançaram uma etapa de agendamento ou visita no Funil SDR."
          icon="ri-calendar-check-line"
          label="Visitas agendadas"
          metricKey="scheduled_visits"
          value={formatNumber(kpis?.scheduled_visits ?? 0)}
          variations={variations}
        />
        <CommercialKpiCard
          aux="Tempo médio até etapa final (agendamento)"
          hint="Tempo médio entre a criação do lead e sua primeira etapa de agendamento ou visita."
          icon="ri-timer-line"
          improvement="decrease"
          label="Tempo médio de ciclo"
          metricKey="avg_cycle_days"
          value={formatDurationDays(kpis?.avg_cycle_days ?? null)}
          variations={variations}
        />
        <CommercialKpiCard
          aux={`${formatNumber(kpis?.scheduled_visits ?? 0)} leads`}
          hint="Percentual dos novos leads que alcançaram a etapa de agendamento ou visita."
          icon="ri-percent-line"
          label="Conversão global"
          metricKey="global_conversion_rate_pct"
          value={formatPercent(kpis?.global_conversion_rate_pct ?? null)}
          variations={variations}
        />
      </div>

      <TeamGoalGaugeCard
        configuration={goalConfiguration}
        errorMessage={goalTrackingError}
        isLoading={goalTrackingLoading}
        progress={goalProgress}
      />

      {funnelLoading ? (
        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <div className="h-5 w-36 animate-pulse rounded bg-muted" />
          <div className="mt-4 h-80 animate-pulse rounded-lg bg-muted/60" />
        </section>
      ) : (
        <CommercialFunnelChart data={funnel} />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <RankingCard
          items={data?.rankings.volume ?? []}
          metric="volume"
          onDetails={onDetails}
          relevantTotal={data?.totals.total_leads ?? 0}
          title="Top 5 canais por volume"
        />
        <RankingCard
          items={data?.rankings.approval ?? []}
          metric="approval"
          onDetails={onDetails}
          relevantTotal={data?.totals.sql_leads ?? 0}
          title="Top 5 canais por aprovação"
        />
        <RankingCard
          items={data?.rankings.mql ?? []}
          metric="mql"
          onDetails={onDetails}
          relevantTotal={data?.totals.mql_leads ?? 0}
          title="Top 5 canais por MQL (CPF Limpo)"
        />
      </div>
    </div>
  );
}

function CommercialSankeyFlow({ links }: { links: CommercialSankeyLinkItem[] }) {
  const [hover, setHover] = useState<{ key: string; type: 'link' | 'node' } | null>(null);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const graph = useMemo(() => buildCommercialSankeyGraph(links), [links]);

  const isNodeDimmed = useCallback((node: CommercialSankeyNode) => {
    if (!hover) return false;
    if (hover.type === 'node') {
      if (node.id === hover.key) return false;
      return !graph.links.some((link) => {
        const source = link.source as CommercialSankeyNode;
        const target = link.target as CommercialSankeyNode;
        return (source.id === hover.key && target.id === node.id) || (target.id === hover.key && source.id === node.id);
      });
    }

    const [sourceKey, targetKey] = hover.key.split('->');
    return node.id !== sourceKey && node.id !== targetKey;
  }, [graph.links, hover]);

  const isLinkDimmed = useCallback((link: CommercialSankeyLink) => {
    if (!hover) return false;
    const source = link.source as CommercialSankeyNode;
    const target = link.target as CommercialSankeyNode;
    if (hover.type === 'node') return source.id !== hover.key && target.id !== hover.key;
    return `${source.id}->${target.id}` !== hover.key;
  }, [hover]);

  if (graph.nodes.length === 0 || graph.links.length === 0) {
    return <HabitaEmptyState icon="ri-flow-chart" title="Sem fluxo para exibir" description="Não há caminhos suficientes nos filtros atuais." />;
  }

  return (
    <div className="space-y-3">
      <div className="relative overflow-x-auto rounded-lg border border-border-subtle bg-background/70 p-3">
        <svg
          viewBox={`0 0 ${COMMERCIAL_SANKEY_WIDTH} ${COMMERCIAL_SANKEY_HEIGHT}`}
          className="w-full text-textPrimary"
          style={{ minWidth: 720 }}
        >
          <g>
            {graph.links.map((link) => {
              const source = link.source as CommercialSankeyNode;
              const target = link.target as CommercialSankeyNode;
              const key = `${source.id}->${target.id}`;
              const dimmed = isLinkDimmed(link);

              return (
                <path
                  key={key}
                  d={sankeyLinkHorizontal<CommercialSankeyNodeDatum, CommercialSankeyLinkDatum>()(link) ?? undefined}
                  fill="none"
                  stroke={link.color}
                  strokeOpacity={dimmed ? 0.07 : hover ? 0.56 : 0.34}
                  strokeWidth={Math.max(1, link.width ?? 1)}
                  className="cursor-pointer transition-[stroke-opacity] duration-150"
                  onMouseEnter={(event) => {
                    setHover({ key, type: 'link' });
                    setTooltip({
                      text: `${source.label} -> ${target.label}: ${formatNumber(link.value)}`,
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                  onMouseMove={(event) => {
                    setTooltip((current) => current ? { ...current, x: event.clientX, y: event.clientY } : current);
                  }}
                  onMouseLeave={() => {
                    setHover(null);
                    setTooltip(null);
                  }}
                />
              );
            })}
          </g>

          <g>
            {graph.nodes.map((node) => {
              const x0 = node.x0 ?? 0;
              const x1 = node.x1 ?? 0;
              const y0 = node.y0 ?? 0;
              const y1 = node.y1 ?? 0;
              const outcome = node.kind === 'outcome';
              const dimmed = isNodeDimmed(node);
              const labelX = outcome ? x0 - 8 : x1 + 8;

              return (
                <g key={node.id} className="transition-opacity duration-150" opacity={dimmed ? 0.24 : 1}>
                  <rect
                    x={x0}
                    y={y0}
                    width={Math.max(1, x1 - x0)}
                    height={Math.max(1, y1 - y0)}
                    rx={2}
                    fill={node.color}
                    className="cursor-pointer"
                    onMouseEnter={(event) => {
                      setHover({ key: node.id, type: 'node' });
                      setTooltip({
                        text: `${node.label}: ${formatNumber(node.value ?? 0)}`,
                        x: event.clientX,
                        y: event.clientY,
                      });
                    }}
                    onMouseMove={(event) => {
                      setTooltip((current) => current ? { ...current, x: event.clientX, y: event.clientY } : current);
                    }}
                    onMouseLeave={() => {
                      setHover(null);
                      setTooltip(null);
                    }}
                  />
                  <text
                    x={labelX}
                    y={(y0 + y1) / 2}
                    dy="0.35em"
                    textAnchor={outcome ? 'end' : 'start'}
                    className="fill-current font-instrument font-semibold"
                    style={{ fontSize: 10 }}
                  >
                    {node.label} · {formatNumber(node.value ?? 0)}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {tooltip && (
          <HabitaFloatingTooltip point={{ x: tooltip.x, y: tooltip.y }}>
            <HabitaChartTooltipSurface className="max-w-[260px]">
              {tooltip.text}
            </HabitaChartTooltipSurface>
          </HabitaFloatingTooltip>
        )}
      </div>

      <div className="flex flex-wrap gap-3 font-instrument text-xs text-textSecondary">
        {[
          ['Aprovado', commercialSankeyOutcomeColor('Aprovado')],
          ['Aprovado c/ condicional', commercialSankeyOutcomeColor('Aprovado com condicional')],
          ['Reprovado', commercialSankeyOutcomeColor('Reprovado')],
          ['Desqualificado', commercialSankeyOutcomeColor('Desqualificado')],
          ['Em andamento', commercialSankeyOutcomeColor('Em andamento')],
        ].map(([label, color]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ChannelsQualityTab({
  dataByWidget,
  errorMessage,
  renderFilterControl,
  renderFilterSummary,
}: {
  dataByWidget: Partial<Record<ChannelWidgetKey, CommercialChannelsQualityResponse | null>>;
  errorMessage: string;
  renderFilterControl: (widgetKey: CommercialWidgetKey) => ReactNode;
  renderFilterSummary: (widgetKey: CommercialWidgetKey) => ReactNode;
}) {
  type ChannelSortKey = 'approval_rate_pct' | 'channel' | 'cpf_clean_rate_pct' | 'cpf_known_rate_pct' | 'detail' | 'total_leads';
  const [sortKey, setSortKey] = useState<ChannelSortKey>('total_leads');
  const [sortAscending, setSortAscending] = useState(false);
  const sourceRows = dataByWidget['channels-ranking']?.channels ?? [];
  const tableRows = useMemo(() => [...sourceRows].sort((left, right) => {
    const valueFor = (item: CommercialChannelRankingItem, key: ChannelSortKey): number | string | null => {
      if (key === 'cpf_known_rate_pct') {
        return 'cpf_known_rate_pct' in item && typeof item.cpf_known_rate_pct === 'number'
          ? item.cpf_known_rate_pct
          : null;
      }
      return item[key];
    };
    const leftValue = valueFor(left, sortKey);
    const rightValue = valueFor(right, sortKey);

    if (typeof leftValue === 'number' || typeof rightValue === 'number') {
      const leftNumber = typeof leftValue === 'number' ? leftValue : Number.NEGATIVE_INFINITY;
      const rightNumber = typeof rightValue === 'number' ? rightValue : Number.NEGATIVE_INFINITY;
      return sortAscending ? leftNumber - rightNumber : rightNumber - leftNumber;
    }

    const comparison = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'pt-BR');
    return sortAscending ? comparison : -comparison;
  }), [sortAscending, sortKey, sourceRows]);
  const cpfRows = dataByWidget['channels-cpf-quality']?.cpf_quality ?? [];
  const sankeyLinks = dataByWidget['channels-flow']?.sankey.links ?? [];

  function changeSort(nextKey: ChannelSortKey) {
    if (nextKey === sortKey) {
      setSortAscending((current) => !current);
      return;
    }
    setSortKey(nextKey);
    setSortAscending(nextKey === 'channel' || nextKey === 'detail');
  }

  const sortableColumns: Array<{ align?: 'right'; key: ChannelSortKey; label: string }> = [
    { key: 'channel', label: 'Canal' },
    { key: 'detail', label: 'Detalhamento' },
    { align: 'right', key: 'total_leads', label: 'Volume' },
    { align: 'right', key: 'approval_rate_pct', label: 'Conversão' },
    { align: 'right', key: 'cpf_known_rate_pct', label: '% CPF conhecido' },
    { align: 'right', key: 'cpf_clean_rate_pct', label: '% CPF limpo' },
  ];

  return (
    <div className="space-y-5">
      {errorMessage && <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>}
      <section className={habitaSurfaceClass}>
        <HabitaSectionHeader title="Ranking de canais por qualidade">
          {renderFilterControl('channels-ranking')}
        </HabitaSectionHeader>
        {renderFilterSummary('channels-ranking')}
        {tableRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-[760px] w-full border-collapse font-instrument text-sm">
              <thead className="bg-muted/45 text-left text-xs uppercase tracking-wide text-textSecondary">
                <tr>
                  {sortableColumns.map((column) => (
                    <th
                      key={column.key}
                      className={classNames('px-4 py-3 font-medium', column.align === 'right' && 'text-right')}
                    >
                      <button
                        type="button"
                        onClick={() => changeSort(column.key)}
                        className={classNames(
                          'inline-flex items-center gap-1 transition-colors hover:text-textPrimary',
                          column.align === 'right' && 'justify-end'
                        )}
                      >
                        {column.label}
                        <i
                          className={classNames(
                            sortKey === column.key
                              ? sortAscending ? 'ri-sort-asc' : 'ri-sort-desc'
                              : 'ri-expand-up-down-line opacity-50'
                          )}
                          aria-hidden
                        />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {tableRows.map((channel) => (
                  <tr key={`${channel.channel}-${channel.detail}`} className="hover:bg-accent/40">
                    <td className="px-4 py-3 font-medium text-textPrimary">{channel.channel}</td>
                    <td className="px-4 py-3 text-textSecondary">{channel.detail}</td>
                    <td className="px-4 py-3 text-right text-textPrimary">{formatNumber(channel.total_leads)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-300">{formatPercent(channel.approval_rate_pct)}</td>
                    <td className="px-4 py-3 text-right text-textPrimary">
                      {'cpf_known_rate_pct' in channel && typeof channel.cpf_known_rate_pct === 'number'
                        ? formatPercent(channel.cpf_known_rate_pct)
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-right text-textPrimary">{formatPercent(channel.cpf_clean_rate_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <HabitaEmptyState
              icon="ri-bar-chart-2-line"
              title="Sem canais para detalhar"
              description="Ajuste os filtros deste componente para visualizar o ranking."
            />
          </div>
        )}
      </section>

      <section className={habitaSurfaceClass}>
        <HabitaSectionHeader
          title="Fluxo - Canal -> Detalhamento -> Etapa final"
          className="px-4 py-3"
        >
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="font-instrument text-xs font-normal text-textSecondary">Volume por caminho</span>
            {renderFilterControl('channels-flow')}
          </div>
        </HabitaSectionHeader>
        {renderFilterSummary('channels-flow')}
        <div className="p-4">
          {sankeyLinks.length > 0 ? (
            <CommercialSankeyFlow links={sankeyLinks} />
          ) : (
            <HabitaEmptyState icon="ri-flow-chart" title="Sem fluxo para exibir" description="Não há caminhos suficientes nos filtros atuais." />
          )}
        </div>
      </section>

      <section className={habitaSurfaceClass}>
        <HabitaSectionHeader title="Canal x qualidade de CPF" className="px-4 py-3">
          {renderFilterControl('channels-cpf-quality')}
        </HabitaSectionHeader>
        {renderFilterSummary('channels-cpf-quality')}
        {cpfRows.length > 0 ? (
          <div className="overflow-x-auto p-4">
            <table className="min-w-[720px] w-full border-collapse font-instrument text-xs">
              <thead className="text-left uppercase tracking-wide text-textSecondary">
                <tr>
                  <th className="px-2 py-2">Canal</th>
                  <th className="px-2 py-2 text-center">Conhecido + limpo</th>
                  <th className="px-2 py-2 text-center">Conhecido + sujo</th>
                  <th className="px-2 py-2 text-center">Desconhecido</th>
                </tr>
              </thead>
              <tbody>
                {cpfRows.map((row) => (
                  <tr key={row.channel} className="border-t border-border-subtle">
                    <td className="px-2 py-2 font-semibold text-textPrimary">{row.channel}</td>
                    {[row.known_clean_pct, row.known_dirty_pct, row.unknown_pct].map((value, index) => (
                      <td key={index} className="px-2 py-2">
                        <div
                          className="flex h-8 items-center justify-center rounded text-textPrimary ring-1 ring-border-subtle"
                          style={{ backgroundColor: `rgba(23, 70, 42, ${Math.max(0.08, ((value ?? 0) / 100) * 0.65)})` }}
                        >
                          {formatPercent(value)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <HabitaEmptyState
              icon="ri-table-view"
              title="Sem qualidade de CPF por canal"
              description="Ajuste os filtros deste componente para visualizar a distribuição."
            />
          </div>
        )}
      </section>
    </div>
  );
}

const funnelBranchRowClasses: Record<CommercialFunnelOutcomeTone, string> = {
  conditional: 'border-l-amber-400 bg-amber-50/70 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200',
  error: 'border-l-red-400 bg-red-50/70 text-red-800 dark:bg-red-500/10 dark:text-red-200',
  neutral: 'border-l-textSecondary/50 bg-muted text-textSecondary',
  success: 'border-l-emerald-500 bg-emerald-50/70 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200',
};

function FunnelStageTooltip({ stage }: { stage: CommercialFunnelStageItem }) {
  const hasPrevious = stage.previous_volume !== null;

  return (
    <HabitaChartTooltipSurface className="w-72 max-w-[calc(100vw-1rem)] p-3 text-left shadow-2xl ring-1 ring-black/5">
      <div className="space-y-1">
        <p className="font-instrument text-[11px] font-medium uppercase text-textSecondary">Etapa</p>
        <p className="font-instrument text-xs font-semibold text-textPrimary">{stage.stage_name}</p>
      </div>
      <div className="mt-2 border-t border-border-subtle pt-2">
        <p className="font-instrument text-xs text-textSecondary">
          Leads na etapa: <span className="font-semibold text-textPrimary">{formatNumber(stage.volume)}</span>
        </p>
      </div>
      {hasPrevious ? (
        <p className="mt-1 font-instrument text-xs text-red-700 dark:text-red-300">
          Perdidos nesta etapa:{' '}
          <span className="font-semibold">{formatNumber(stage.lost_leads)}</span>
          {' '}({formatPercent(stage.lost_rate_pct)})
        </p>
      ) : (
        <p className="mt-1 font-instrument text-xs text-textSecondary">Etapa de entrada — sem perda.</p>
      )}
    </HabitaChartTooltipSurface>
  );
}

function FunnelBranchGroup({ branches }: { branches: CommercialFunnelBranchItem[] }) {
  if (branches.length === 0) return <div aria-hidden />;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <i className="ri-arrow-right-line shrink-0 text-base text-textSecondary" aria-hidden />
      <div className="min-w-0 flex-1 overflow-hidden rounded-md border border-border-subtle bg-surface shadow-sm">
        {branches.map((branch, index) => {
          const branchDescription = `${branch.label}: ${formatNumber(branch.volume)}, ${formatPercent(branch.share_of_anchor_pct)} do volume de ${branch.anchor_stage_name}`;

          return (
            <div
              key={`${branch.outcome}-${branch.anchor_stage_index}`}
              aria-label={branchDescription}
              className={classNames(
                'grid min-h-8 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 border-l-2 px-2.5 py-1.5 font-instrument text-xs',
                index > 0 && 'border-t border-t-border-subtle',
                funnelBranchRowClasses[branch.tone]
              )}
            >
              <span className="min-w-0 font-semibold leading-tight">{branch.label}</span>
              <span className="font-semibold tabular-nums">{formatNumber(branch.volume)}</span>
              <span className="min-w-12 text-right tabular-nums opacity-80">
                {formatPercent(branch.share_of_anchor_pct)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CommercialFunnelChart({ data }: { data: CommercialFunnelResponse | null }) {
  const [stageTooltip, setStageTooltip] = useState<{
    stage: CommercialFunnelStageItem;
    x: number;
    y: number;
  } | null>(null);
  const stages = data?.stages ?? [];
  const branches = data?.branches ?? [];
  const maxVolume = Math.max(...stages.map((stage) => stage.volume), 1);
  const branchesByAnchor = branches.reduce((acc, branch) => {
    const items = acc.get(branch.anchor_stage_index) ?? [];
    items.push(branch);
    acc.set(branch.anchor_stage_index, items);
    return acc;
  }, new Map<number, CommercialFunnelBranchItem[]>());

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Funil SDR-CRM" className="px-4 py-3">
        <span className="font-instrument text-xs font-medium text-textSecondary">
          {formatNumber(data?.total_leads ?? 0)} leads no período
        </span>
      </HabitaSectionHeader>

      {stages.length === 0 ? (
        <div className="p-4">
          <HabitaEmptyState
            icon="ri-filter-off-line"
            title="Sem etapas para o período"
            description="Ajuste o período ou os filtros para visualizar o funil comercial."
          />
        </div>
      ) : (
        <div className="overflow-x-auto px-4 pb-5 pt-3">
          <div className="min-w-[1040px] space-y-2">
            {stages.map((stage) => {
              const width = stage.volume === 0 ? 0 : Math.max(18, (stage.volume / maxVolume) * 100);
              const stageBranches = branchesByAnchor.get(stage.stage_index) ?? [];

              return (
                <div
                  key={`${stage.stage_index}-${stage.stage_name}`}
                  className="grid grid-cols-[16rem_minmax(24rem,1fr)_22rem] items-center gap-3 py-2.5"
                >
                  <div className="text-right font-instrument text-xs text-textSecondary">
                    <span className="font-medium">{stage.stage_name}</span>
                    {stage.previous_volume !== null && (
                      <span className="ml-1 font-semibold text-textPrimary/75">
                        · {formatPercent(stage.conversion_rate_pct)}
                      </span>
                    )}
                  </div>
                  <div className="relative h-10">
                    <div
                      className="relative flex h-full cursor-default items-center justify-center"
                      onMouseEnter={(event) => setStageTooltip({ stage, x: event.clientX, y: event.clientY })}
                      onMouseMove={(event) => setStageTooltip((current) => (
                        current ? { ...current, x: event.clientX, y: event.clientY } : current
                      ))}
                      onMouseLeave={() => setStageTooltip(null)}
                    >
                      <div
                        className="mx-auto flex h-full cursor-default items-center justify-center rounded-md bg-primary/80 px-3 font-instrument text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary"
                        style={{ width: `${width}%` }}
                      >
                        {formatNumber(stage.volume)}
                      </div>
                    </div>
                  </div>
                  <FunnelBranchGroup branches={stageBranches} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {stageTooltip && (
        <HabitaFloatingTooltip point={{ x: stageTooltip.x, y: stageTooltip.y }}>
          <FunnelStageTooltip stage={stageTooltip.stage} />
        </HabitaFloatingTooltip>
      )}
    </section>
  );
}

const trendGranularityOptions: Array<{ label: string; value: CommercialTrendGranularity }> = [
  { label: 'Mensal', value: 'monthly' },
  { label: 'Semanal', value: 'weekly' },
  { label: 'Dia', value: 'daily' },
];

function TrendGranularitySelect({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (value: CommercialTrendGranularity) => void;
  value: CommercialTrendGranularity;
}) {
  return (
    <HabitaSelect
      aria-label={label}
      className="h-9 min-w-28 rounded-md border border-border-subtle bg-surface px-3 font-instrument text-xs font-medium text-textPrimary outline-none transition-colors focus:border-primary"
      options={trendGranularityOptions}
      value={value}
      onChange={(event) => onChange(event.target.value as CommercialTrendGranularity)}
    />
  );
}

type CommercialTrendChartType = 'bar' | 'line';

interface IconViewOption<TValue extends string> {
  icon: string;
  label: string;
  value: TValue;
}

const trendChartTypeOptions: Array<IconViewOption<CommercialTrendChartType>> = [
  { icon: 'ri-bar-chart-2-line', label: 'Barras', value: 'bar' },
  { icon: 'ri-line-chart-line', label: 'Linhas', value: 'line' },
];

function IconViewToggle<TValue extends string>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: TValue) => void;
  options: Array<IconViewOption<TValue>>;
  value: TValue;
}) {
  return (
    <div
      aria-label={label}
      className="inline-flex h-9 shrink-0 items-center rounded-md border border-border-subtle bg-muted/45 p-0.5"
      role="group"
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-label={`Exibir como ${option.label.toLowerCase()}`}
            aria-pressed={isActive}
            className={classNames(
              'inline-flex h-8 w-8 items-center justify-center rounded transition-colors',
              isActive
                ? 'bg-primary text-white shadow-sm'
                : 'text-textSecondary hover:bg-surface hover:text-textPrimary'
            )}
            onClick={() => onChange(option.value)}
          >
            <i className={`${option.icon} text-base`} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

function TrendChartTypeToggle({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (value: CommercialTrendChartType) => void;
  value: CommercialTrendChartType;
}) {
  return (
    <IconViewToggle
      label={label}
      onChange={onChange}
      options={trendChartTypeOptions}
      value={value}
    />
  );
}

interface ApprovalEvolutionTooltipRow {
  approved_count: number;
  created_in_period_count: number;
  period_start: string;
  scheduled_visits_count: number;
}

function ApprovalEvolutionTooltip({
  active,
  granularity,
  payload,
}: {
  active?: boolean;
  granularity: CommercialTrendGranularity;
  payload?: Array<{ payload?: ApprovalEvolutionTooltipRow }>;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  return (
    <HabitaChartTooltipSurface className="min-w-52 p-3">
      <p className="font-instrument text-sm font-semibold text-textPrimary">
        {formatTrendPeriodLabel(row.period_start, granularity)}
      </p>
      <div className="mt-2 space-y-1 font-instrument text-xs">
        <p className="flex items-center justify-between gap-5">
          <span className="text-textSecondary">Pastas aprovadas</span>
          <strong className="text-textPrimary">{formatNumber(row.approved_count)}</strong>
        </p>
        <p className="flex items-center justify-between gap-5">
          <span className="text-textSecondary">Visitas agendadas</span>
          <strong className="text-textPrimary">{formatNumber(row.scheduled_visits_count)}</strong>
        </p>
        <p className="flex items-center justify-between gap-5">
          <span className="text-textSecondary">{createdInPeriodLabel(granularity)}</span>
          <strong className="text-textPrimary">{formatNumber(row.created_in_period_count)}</strong>
        </p>
      </div>
    </HabitaChartTooltipSurface>
  );
}

type CycleTrendSeriesKey = 'all' | 'lost' | 'won';

const cycleTrendLegend: Array<{
  color: string;
  key: CycleTrendSeriesKey;
  label: string;
}> = [
  { color: '#17462a', key: 'all', label: 'Todos os desfechos' },
  { color: '#2f8f5b', key: 'won', label: 'Ganho' },
  { color: '#d94a3a', key: 'lost', label: 'Perda' },
];

type StuckLeadsView = 'chart' | 'table';

type StuckLeadsChartRow = {
  sdr_name: string;
  total: number;
} & Record<CommercialOperationStuckLeadStatus, number>;

const stuckLeadsViewOptions: Array<IconViewOption<StuckLeadsView>> = [
  { icon: 'ri-table-line', label: 'Tabela', value: 'table' },
  { icon: 'ri-bar-chart-2-line', label: 'Gráfico', value: 'chart' },
];

const stuckLeadCategories: Array<{
  badgeClass: string;
  color: string;
  key: CommercialOperationStuckLeadStatus;
  label: string;
  thresholdLabel: string;
}> = [
  {
    badgeClass: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-200',
    color: '#eab308',
    key: 'attention',
    label: 'Atenção',
    thresholdLabel: '+1 dia',
  },
  {
    badgeClass: 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200',
    color: '#f59e0b',
    key: 'alert',
    label: 'Alerta',
    thresholdLabel: '+3 dias',
  },
  {
    badgeClass: 'bg-orange-50 text-orange-800 dark:bg-orange-500/10 dark:text-orange-200',
    color: '#f97316',
    key: 'urgent',
    label: 'Urgente',
    thresholdLabel: '+7 dias',
  },
  {
    badgeClass: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
    color: '#dc2626',
    key: 'critical',
    label: 'Crítico',
    thresholdLabel: '+15 dias',
  },
  {
    badgeClass: 'bg-rose-100 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
    color: '#7f1d1d',
    key: 'stagnant',
    label: 'Estagnado',
    thresholdLabel: '+30 dias',
  },
];

const stuckLeadCategoryByStatus = Object.fromEntries(
  stuckLeadCategories.map((category) => [category.key, category])
) as Record<CommercialOperationStuckLeadStatus, typeof stuckLeadCategories[number]>;

const stuckLeadsPageSizeOptions = [10, 25, 50];

function createStuckLeadsChartRows(
  leads: CommercialOperationStuckLeadItem[]
): StuckLeadsChartRow[] {
  const rowsBySdr = new Map<string, StuckLeadsChartRow>();

  leads.forEach((lead) => {
    const row = rowsBySdr.get(lead.sdr_name) ?? {
      alert: 0,
      attention: 0,
      critical: 0,
      sdr_name: lead.sdr_name,
      stagnant: 0,
      total: 0,
      urgent: 0,
    };
    row[lead.status] += 1;
    row.total += 1;
    rowsBySdr.set(lead.sdr_name, row);
  });

  return Array.from(rowsBySdr.values())
    .sort((a, b) => b.total - a.total || a.sdr_name.localeCompare(b.sdr_name, 'pt-BR'));
}

function StuckLeadsChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: StuckLeadsChartRow }>;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  return (
    <HabitaChartTooltipSurface className="min-w-64 p-3">
      <p className="font-instrument text-sm font-semibold text-textPrimary">{row.sdr_name}</p>
      <div className="mt-2 flex items-center justify-between gap-6 border-b border-border-subtle pb-2 font-instrument text-xs">
        <span className="text-textSecondary">Total de leads parados</span>
        <span className="font-semibold text-textPrimary">{formatNumber(row.total)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {stuckLeadCategories.map((category) => {
          const value = row[category.key];
          const share = row.total > 0 ? (value / row.total) * 100 : 0;

          return (
            <div key={category.key} className="flex items-center justify-between gap-6 font-instrument text-xs">
              <span className="flex items-center gap-1.5 text-textSecondary">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: category.color }}
                  aria-hidden
                />
                {category.label}
              </span>
              <span className="whitespace-nowrap font-semibold text-textPrimary">
                {formatNumber(value)} · {formatPercent(share)}
              </span>
            </div>
          );
        })}
      </div>
    </HabitaChartTooltipSurface>
  );
}

function StuckLeadsChart({ rows }: { rows: StuckLeadsChartRow[] }) {
  return (
    <div className="overflow-x-auto px-4 pb-4">
      <div
        className="h-80"
        style={{ width: `max(100%, ${Math.max(720, rows.length * 104)}px)` }}
      >
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart data={rows} margin={{ bottom: 50, left: 0, right: 12, top: 28 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" vertical={false} />
            <XAxis
              dataKey="sdr_name"
              interval={0}
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickFormatter={(value) => {
                const label = String(value);
                return label.length > 18 ? `${label.slice(0, 17)}…` : label;
              }}
              tickLine={false}
              angle={-20}
              textAnchor="end"
              height={64}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickLine={false}
              width={42}
            />
            <Tooltip
              allowEscapeViewBox={{ x: false, y: false }}
              content={<StuckLeadsChartTooltip />}
              cursor={{ fill: 'rgba(20, 70, 42, 0.05)' }}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
            />
            {stuckLeadCategories.map((category, index) => (
              <Bar
                key={category.key}
                dataKey={category.key}
                name={category.label}
                fill={category.color}
                maxBarSize={56}
                radius={index === stuckLeadCategories.length - 1 ? [4, 4, 0, 0] : undefined}
                stackId="stuck-leads"
              >
                <LabelList
                  {...chartBarLabelProps}
                  position="top"
                  valueAccessor={(entry) => {
                    const row = entry.payload as StuckLeadsChartRow;
                    const hasVisibleSegmentAbove = stuckLeadCategories
                      .slice(index + 1)
                      .some((nextCategory) => row[nextCategory.key] > 0);
                    return hasVisibleSegmentAbove ? '' : formatCountBarLabel(row.total);
                  }}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OperationFunnelTab({
  interestRegions,
  interestRegionsLoading,
  operationMetricsByWidget,
  operationMetricsError,
  renderFilterControl,
  renderFilterSummary,
}: {
  interestRegions: CommercialInterestRegionsResponse | null;
  interestRegionsLoading: boolean;
  operationMetricsByWidget: Partial<Record<OperationWidgetKey, CommercialOperationMetricsResponse | null>>;
  operationMetricsError: string;
  renderFilterControl: (widgetKey: CommercialWidgetKey) => ReactNode;
  renderFilterSummary: (widgetKey: CommercialWidgetKey) => ReactNode;
}) {
  const [approvalGranularity, setApprovalGranularity] = useState<CommercialTrendGranularity>('daily');
  const [approvalChartType, setApprovalChartType] = useState<CommercialTrendChartType>('bar');
  const [conversionGranularity, setConversionGranularity] = useState<CommercialTrendGranularity>('daily');
  const [conversionChartType, setConversionChartType] = useState<CommercialTrendChartType>('bar');
  const [cycleGranularity, setCycleGranularity] = useState<CommercialTrendGranularity>('daily');
  const [cycleChartType, setCycleChartType] = useState<CommercialTrendChartType>('bar');
  const [stuckLeadsView, setStuckLeadsView] = useState<StuckLeadsView>('chart');
  const [stuckLeadsPage, setStuckLeadsPage] = useState(1);
  const [stuckLeadsPageSize, setStuckLeadsPageSize] = useState(10);
  const [visibleCycleSeries, setVisibleCycleSeries] = useState<Record<CycleTrendSeriesKey, boolean>>({
    all: true,
    lost: true,
    won: true,
  });
  const stageTimes = operationMetricsByWidget['operation-stage-time']?.stage_times ?? [];
  const lossReasons = operationMetricsByWidget['operation-loss-reasons']?.loss_reasons ?? [];
  const stuckLeadsMetrics = operationMetricsByWidget['operation-stuck-leads'];
  const stuckLeads = useMemo(
    () => stuckLeadsMetrics?.stuck_leads ?? [],
    [stuckLeadsMetrics]
  );
  const stuckLeadsChartRows = useMemo(
    () => createStuckLeadsChartRows(stuckLeads),
    [stuckLeads]
  );
  const approvalEvolution = operationMetricsByWidget['operation-approval-evolution']
    ?.approval_evolution[approvalGranularity] ?? [];
  const conversionEvolution = operationMetricsByWidget['operation-conversion-evolution']
    ?.conversion_evolution_series[conversionGranularity] ?? [];
  const cycleEvolution = operationMetricsByWidget['operation-cycle-evolution']
    ?.cycle_evolution_series[cycleGranularity] ?? [];
  const visibleCycleSeriesCount = Object.values(visibleCycleSeries).filter(Boolean).length;
  const showApprovalBarLabels = shouldDisplayInlineBarLabels(approvalEvolution.length, 2);
  const showConversionBarLabels = shouldDisplayInlineBarLabels(conversionEvolution.length, 3);
  const showCycleBarLabels = shouldDisplayInlineBarLabels(
    cycleEvolution.length,
    visibleCycleSeriesCount
  );
  const stuckLeadsTotalPages = Math.max(1, Math.ceil(stuckLeads.length / stuckLeadsPageSize));
  const currentStuckLeadsPage = Math.min(stuckLeadsPage, stuckLeadsTotalPages);
  const paginatedStuckLeads = useMemo(() => {
    const start = (currentStuckLeadsPage - 1) * stuckLeadsPageSize;
    return stuckLeads.slice(start, start + stuckLeadsPageSize);
  }, [currentStuckLeadsPage, stuckLeads, stuckLeadsPageSize]);

  useEffect(() => {
    setStuckLeadsPage(1);
  }, [stuckLeadsMetrics]);

  const toggleCycleSeries = (key: CycleTrendSeriesKey) => {
    setVisibleCycleSeries((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <div className="flex flex-col gap-5">
      {operationMetricsError && <HabitaErrorNotice>{operationMetricsError}</HabitaErrorNotice>}

      <section className={classNames(habitaSurfaceClass, 'order-4')}>
        <HabitaSectionHeader title="Tempo médio de permanência por etapa" className="px-4 py-3">
          {renderFilterControl('operation-stage-time')}
        </HabitaSectionHeader>
        {renderFilterSummary('operation-stage-time')}
        <div className="h-80 p-4">
          {stageTimes.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={stageTimes} layout="vertical" margin={{ bottom: 8, left: 96, right: 58, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" />
                <XAxis type="number" tick={{ fill: 'currentColor', fontSize: 11 }} unit="d" />
                <YAxis dataKey="stage_name" type="category" width={170} tick={{ fill: 'currentColor', fontSize: 11 }} />
                <Tooltip
                  allowEscapeViewBox={{ x: false, y: false }}
                  content={<HabitaRechartsTooltip valueFormatter={(value) => `${formatDecimal(Number(value), 1)}d`} />}
                  wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                />
                <Bar dataKey="avg_days" name="Tempo médio" radius={[0, 4, 4, 0]}>
                  {stageTimes.map((item) => (
                    <Cell
                      key={item.stage_index}
                      fill={(item.avg_days ?? 0) >= 5 ? '#d94a3a' : (item.avg_days ?? 0) >= 3 ? '#d9a322' : '#2f8f5b'}
                    />
                  ))}
                  <LabelList
                    {...chartBarLabelProps}
                    formatter={formatDaysBarLabel}
                    position="right"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <HabitaEmptyState icon="ri-bar-chart-horizontal-line" title="Sem tempos por etapa" description="Não há histórico suficiente para os filtros atuais." />
          )}
        </div>
      </section>

      <InterestRegionsCard
        data={interestRegions}
        filterControl={renderFilterControl('operation-interest-regions')}
        filterSummary={renderFilterSummary('operation-interest-regions')}
        isLoading={interestRegionsLoading}
      />

      <section className={classNames(habitaSurfaceClass, 'order-6')}>
        <HabitaSectionHeader title="Principais motivos de perda no funil" className="px-4 py-3">
          {renderFilterControl('operation-loss-reasons')}
        </HabitaSectionHeader>
        {renderFilterSummary('operation-loss-reasons')}
        <div className="space-y-3 p-4">
          {lossReasons.length > 0 ? lossReasons.map((item) => {
            const variationTone = comparisonTone(item.variation_pct, 'decrease');
            return (
              <div key={item.reason} className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 font-instrument text-xs">
                  <span className="min-w-0 font-semibold text-textPrimary">{item.reason}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <strong className="text-textPrimary">{formatNumber(item.volume)}</strong>
                    <span className="text-textSecondary">{formatPercent(item.share_pct)}</span>
                    <span className={classNames(
                      'font-semibold',
                      variationTone === 'positive' && 'text-emerald-700 dark:text-emerald-300',
                      variationTone === 'negative' && 'text-red-700 dark:text-red-300',
                      variationTone === 'neutral' && 'text-textSecondary'
                    )}>
                      {formatSignedPercent(item.variation_pct)}
                    </span>
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-red-500/70"
                    style={{ width: `${Math.max(0, Math.min(item.share_pct ?? 0, 100))}%` }}
                  />
                </div>
              </div>
            );
          }) : (
            <HabitaEmptyState
              icon="ri-error-warning-line"
              title="Sem motivos de perda"
              description="Não há perdas registradas com os filtros atuais."
            />
          )}
        </div>
      </section>

      <section className={classNames(habitaSurfaceClass, 'order-7')}>
        <HabitaSectionHeader title="Leads parados" className="px-4 py-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {renderFilterControl('operation-stuck-leads')}
            <IconViewToggle
              label="Visualização dos leads parados"
              onChange={setStuckLeadsView}
              options={stuckLeadsViewOptions}
              value={stuckLeadsView}
            />
          </div>
        </HabitaSectionHeader>
        {renderFilterSummary('operation-stuck-leads')}
        {stuckLeads.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-border-subtle px-4 py-3 font-instrument text-xs text-textSecondary">
            {stuckLeadCategories.map((category) => (
              <span key={category.key}>
                <i
                  className="ri-checkbox-blank-circle-fill"
                  style={{ color: category.color }}
                  aria-hidden
                />{' '}
                {category.label} ({category.thresholdLabel})
              </span>
            ))}
          </div>
        )}
        {stuckLeads.length === 0 ? (
          <div className="border-t border-border-subtle p-4">
            <HabitaEmptyState icon="ri-user-search-line" title="Sem leads parados" description="Não há oportunidades abertas para destacar nos filtros atuais." />
          </div>
        ) : stuckLeadsView === 'chart' ? (
          <StuckLeadsChart rows={stuckLeadsChartRows} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[840px] w-full border-collapse font-instrument text-sm">
                <thead className="bg-muted/45 text-left text-xs uppercase tracking-wide text-textSecondary">
                  <tr>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-4 py-3">Etapa atual</th>
                    <th className="px-4 py-3 text-right">Dias sem contato</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">SDR responsável</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {paginatedStuckLeads.map((lead) => {
                    const category = stuckLeadCategoryByStatus[lead.status];

                    return (
                      <tr key={lead.opportunity_id} className="hover:bg-accent/40">
                        <td className="px-4 py-3 font-semibold text-textPrimary">{lead.lead_name}</td>
                        <td className="px-4 py-3 text-textSecondary">{lead.stage_name}</td>
                        <td className="px-4 py-3 text-right text-textPrimary">
                          {formatNumber(lead.days_without_contact)}d
                        </td>
                        <td className="px-4 py-3">
                          <span className={classNames(
                            'rounded-full px-2 py-1 text-xs font-semibold',
                            category.badgeClass
                          )}>
                            {category.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-textSecondary">{lead.sdr_name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border-subtle px-4 py-3">
              <HabitaPagination
                currentPage={currentStuckLeadsPage}
                onPageChange={setStuckLeadsPage}
                onPageSizeChange={(pageSize) => {
                  setStuckLeadsPageSize(pageSize);
                  setStuckLeadsPage(1);
                }}
                pageSize={stuckLeadsPageSize}
                pageSizeOptions={stuckLeadsPageSizeOptions}
                totalItems={stuckLeads.length}
                totalPages={stuckLeadsTotalPages}
              />
            </div>
          </>
        )}
      </section>

      <section className={classNames(habitaSurfaceClass, 'order-1')}>
        <HabitaSectionHeader
          title="Pastas aprovadas e visitas agendadas por mês"
          description="Volume agregado de pastas com desfecho Aprovado"
          className="px-4 py-3"
        >
          <div className="flex flex-wrap items-center justify-end gap-2">
            {renderFilterControl('operation-approval-evolution')}
            <TrendChartTypeToggle
              label="Visualização de pastas aprovadas e visitas agendadas"
              value={approvalChartType}
              onChange={setApprovalChartType}
            />
            <TrendGranularitySelect
              label="Granularidade de pastas aprovadas e visitas agendadas"
              value={approvalGranularity}
              onChange={setApprovalGranularity}
            />
          </div>
        </HabitaSectionHeader>
        {renderFilterSummary('operation-approval-evolution')}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-4 font-instrument text-xs text-textSecondary"
          aria-label="Séries de pastas aprovadas e visitas agendadas"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#2e7bb4]" aria-hidden />
            Visitas agendadas por mês
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#17462a]" aria-hidden />
            Pastas aprovadas por mês
          </span>
        </div>
        <div className="h-72 p-4">
          {approvalEvolution.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <ComposedChart
                data={approvalEvolution}
                margin={{ bottom: 8, left: 0, right: 12, top: showApprovalBarLabels ? 28 : 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" />
                <XAxis
                  dataKey="period_start"
                  minTickGap={24}
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  tickFormatter={(value) => formatTrendAxisLabel(String(value), approvalGranularity)}
                />
                <YAxis allowDecimals={false} tick={{ fill: 'currentColor', fontSize: 11 }} />
                <Tooltip
                  allowEscapeViewBox={{ x: false, y: false }}
                  content={<ApprovalEvolutionTooltip granularity={approvalGranularity} />}
                  wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                />
                {approvalChartType === 'bar' ? (
                  <>
                    <Bar
                      dataKey="scheduled_visits_count"
                      fill="#2e7bb4"
                      maxBarSize={36}
                      name="Visitas agendadas"
                      radius={[4, 4, 0, 0]}
                    >
                      {showApprovalBarLabels && (
                        <LabelList
                          {...chartBarLabelProps}
                          formatter={formatCountBarLabel}
                          position="top"
                        />
                      )}
                    </Bar>
                    <Bar
                      dataKey="approved_count"
                      fill="#17462a"
                      maxBarSize={36}
                      name="Pastas aprovadas"
                      radius={[4, 4, 0, 0]}
                    >
                      {showApprovalBarLabels && (
                        <LabelList
                          {...chartBarLabelProps}
                          formatter={formatCountBarLabel}
                          position="top"
                        />
                      )}
                    </Bar>
                  </>
                ) : (
                  <>
                    <Line
                      type="monotone"
                      dataKey="scheduled_visits_count"
                      name="Visitas agendadas"
                      stroke="#2e7bb4"
                      strokeWidth={2.2}
                      dot={approvalEvolution.length === 1 ? { r: 4 } : false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="approved_count"
                      name="Pastas aprovadas"
                      stroke="#17462a"
                      strokeWidth={2.2}
                      dot={approvalEvolution.length === 1 ? { r: 4 } : false}
                      activeDot={{ r: 4 }}
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <HabitaEmptyState icon="ri-bar-chart-2-line" title="Sem dados no período" description="Não há aprovações ou visitas agendadas no recorte disponível." />
          )}
        </div>
      </section>

      <div className="contents">
        <section className={classNames(habitaSurfaceClass, 'order-2')}>
          <HabitaSectionHeader
            title="Evolução das taxas de conversão"
            description="Tendência de taxa de conversão agregada do time"
            className="px-4 py-3"
          >
            <div className="flex flex-wrap items-center justify-end gap-2">
              {renderFilterControl('operation-conversion-evolution')}
              <TrendChartTypeToggle
                label="Visualização da evolução das taxas de conversão"
                value={conversionChartType}
                onChange={setConversionChartType}
              />
              <TrendGranularitySelect
                label="Granularidade da evolução das taxas de conversão"
                value={conversionGranularity}
                onChange={setConversionGranularity}
              />
            </div>
          </HabitaSectionHeader>
          {renderFilterSummary('operation-conversion-evolution')}
          <div className="h-72 p-4">
            {conversionEvolution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <ComposedChart
                  data={conversionEvolution}
                  margin={{ bottom: 8, left: 0, right: 12, top: showConversionBarLabels ? 28 : 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" />
                  <XAxis
                    dataKey="period_start"
                    minTickGap={24}
                    tick={{ fill: 'currentColor', fontSize: 11 }}
                    tickFormatter={(value) => formatTrendAxisLabel(String(value), conversionGranularity)}
                  />
                  <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} unit="%" />
                  <Tooltip
                    allowEscapeViewBox={{ x: false, y: false }}
                    content={(
                      <HabitaRechartsTooltip
                        labelFormatter={(label) => formatTrendPeriodLabel(String(label ?? ''), conversionGranularity)}
                        valueFormatter={(value) => formatPercent(Number(value))}
                      />
                    )}
                    wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                  />
                  {conversionChartType === 'bar' ? (
                    <>
                      <Bar dataKey="cpf_clean_to_folder_rate_pct" name="CPF Limpo → Pasta" fill="#2e7bb4" maxBarSize={28} radius={[3, 3, 0, 0]}>
                        {showConversionBarLabels && (
                          <LabelList {...chartBarLabelProps} formatter={formatPercentBarLabel} position="top" />
                        )}
                      </Bar>
                      <Bar dataKey="folder_to_approved_rate_pct" name="Pasta → Aprovação" fill="#2f8f5b" maxBarSize={28} radius={[3, 3, 0, 0]}>
                        {showConversionBarLabels && (
                          <LabelList {...chartBarLabelProps} formatter={formatPercentBarLabel} position="top" />
                        )}
                      </Bar>
                      <Bar dataKey="global_conversion_rate_pct" name="Conversão global" fill="#d9a322" maxBarSize={28} radius={[3, 3, 0, 0]}>
                        {showConversionBarLabels && (
                          <LabelList {...chartBarLabelProps} formatter={formatPercentBarLabel} position="top" />
                        )}
                      </Bar>
                    </>
                  ) : (
                    <>
                      <Line type="monotone" dataKey="cpf_clean_to_folder_rate_pct" name="CPF Limpo → Pasta" stroke="#2e7bb4" strokeWidth={2} dot={conversionEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                      <Line type="monotone" dataKey="folder_to_approved_rate_pct" name="Pasta → Aprovação" stroke="#2f8f5b" strokeWidth={2} dot={conversionEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                      <Line type="monotone" dataKey="global_conversion_rate_pct" name="Conversão global" stroke="#d9a322" strokeWidth={2} dot={conversionEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <HabitaEmptyState icon="ri-bar-chart-2-line" title="Sem evolução de conversão" description="Não há dados suficientes para montar a evolução." />
            )}
          </div>
        </section>

        <section className={classNames(habitaSurfaceClass, 'order-3')}>
          <HabitaSectionHeader
            title="Evolução do tempo médio de ciclo"
            description="Tempo médio agregado para desfechos (Ganho/Perda)"
            className="px-4 py-3"
          >
            <div className="flex flex-wrap items-center justify-end gap-2">
              {renderFilterControl('operation-cycle-evolution')}
              <TrendChartTypeToggle
                label="Visualização da evolução do tempo médio de ciclo"
                value={cycleChartType}
                onChange={setCycleChartType}
              />
              <TrendGranularitySelect
                label="Granularidade da evolução do tempo médio de ciclo"
                value={cycleGranularity}
                onChange={setCycleGranularity}
              />
            </div>
          </HabitaSectionHeader>
          {renderFilterSummary('operation-cycle-evolution')}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-4" aria-label="Séries do tempo médio de ciclo">
            {cycleTrendLegend.map((item) => {
              const isVisible = visibleCycleSeries[item.key];
              return (
                <button
                  key={item.key}
                  type="button"
                  aria-pressed={isVisible}
                  className={classNames(
                    'inline-flex items-center gap-1.5 font-instrument text-xs font-medium transition-opacity',
                    isVisible ? 'text-textPrimary' : 'text-textSecondary opacity-50'
                  )}
                  onClick={() => toggleCycleSeries(item.key)}
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} aria-hidden />
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="h-72 p-4 pt-2">
            {cycleEvolution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <ComposedChart
                  data={cycleEvolution}
                  margin={{ bottom: 8, left: 0, right: 12, top: showCycleBarLabels ? 28 : 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" />
                  <XAxis
                    dataKey="period_start"
                    minTickGap={24}
                    tick={{ fill: 'currentColor', fontSize: 11 }}
                    tickFormatter={(value) => formatTrendAxisLabel(String(value), cycleGranularity)}
                  />
                  <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} unit="d" />
                  <Tooltip
                    allowEscapeViewBox={{ x: false, y: false }}
                    content={(
                      <HabitaRechartsTooltip
                        labelFormatter={(label) => formatTrendPeriodLabel(String(label ?? ''), cycleGranularity)}
                        valueFormatter={(value) => `${formatDecimal(Number(value), 1)}d`}
                      />
                    )}
                    wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                  />
                  {cycleChartType === 'bar' ? (
                    <>
                      {visibleCycleSeries.all && (
                        <Bar dataKey="avg_cycle_days" name="Todos os desfechos" fill="#17462a" maxBarSize={28} radius={[3, 3, 0, 0]}>
                          {showCycleBarLabels && (
                            <LabelList {...chartBarLabelProps} formatter={formatDaysBarLabel} position="top" />
                          )}
                        </Bar>
                      )}
                      {visibleCycleSeries.won && (
                        <Bar dataKey="won_avg_cycle_days" name="Ganho" fill="#2f8f5b" maxBarSize={28} radius={[3, 3, 0, 0]}>
                          {showCycleBarLabels && (
                            <LabelList {...chartBarLabelProps} formatter={formatDaysBarLabel} position="top" />
                          )}
                        </Bar>
                      )}
                      {visibleCycleSeries.lost && (
                        <Bar dataKey="lost_avg_cycle_days" name="Perda" fill="#d94a3a" maxBarSize={28} radius={[3, 3, 0, 0]}>
                          {showCycleBarLabels && (
                            <LabelList {...chartBarLabelProps} formatter={formatDaysBarLabel} position="top" />
                          )}
                        </Bar>
                      )}
                    </>
                  ) : (
                    <>
                      {visibleCycleSeries.all && (
                        <Line type="monotone" dataKey="avg_cycle_days" name="Todos os desfechos" stroke="#17462a" strokeWidth={3} dot={cycleEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                      )}
                      {visibleCycleSeries.won && (
                        <Line type="monotone" dataKey="won_avg_cycle_days" name="Ganho" stroke="#2f8f5b" strokeWidth={2} dot={cycleEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                      )}
                      {visibleCycleSeries.lost && (
                        <Line type="monotone" dataKey="lost_avg_cycle_days" name="Perda" stroke="#d94a3a" strokeWidth={2} dot={cycleEvolution.length === 1 ? { r: 4 } : false} activeDot={{ r: 4 }} />
                      )}
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <HabitaEmptyState icon="ri-timer-line" title="Sem evolução de ciclo" description="Não há desfechos com tempo calculável no recorte." />
            )}
          </div>
        </section>
      </div>

    </div>
  );
}

const sdrGoalStatusClass: Record<CommercialSdrGoalItem['status'], string> = {
  attention: 'text-textSecondary',
  done: 'text-emerald-700 dark:text-emerald-300',
  no_goal: 'text-textSecondary',
  on_track: 'text-textSecondary',
};

const sdrGoalFillClass: Record<CommercialSdrGoalItem['status'], string> = {
  attention: 'bg-primary',
  done: 'bg-emerald-600',
  no_goal: 'bg-muted-foreground/40',
  on_track: 'bg-primary',
};

const sdrGoalTypeLabels: Record<GoalType, string> = {
  PASTAS_APROVADAS: 'Pastas aprovadas',
  PASTAS_GERADAS: 'Pastas geradas',
  VISITAS_AGENDADAS: 'Visitas agendadas',
};

function SdrGoalRow({ item, scaleMax }: { item: CommercialSdrGoalItem; scaleMax: number }) {
  const achievedWidth = Math.min(100, Math.max(0, (item.achieved / scaleMax) * 100));
  const markerPosition = Math.min(100, Math.max(0, (item.monthly_goal / scaleMax) * 100));
  const progressLabel = item.progress_pct === null ? '-' : `${formatDecimal(item.progress_pct, 1)}%`;

  return (
    <div className="space-y-1.5">
      <div className="flex min-w-0 items-center justify-between gap-3 font-instrument text-xs">
        <p className="truncate font-medium text-textPrimary">{item.sdr}</p>
        <div className="flex shrink-0 items-center gap-2 font-instrument text-xs">
          <span className="font-semibold text-textPrimary">{formatNumber(item.achieved)}</span>
          <span className="text-textSecondary">/ {formatNumber(item.monthly_goal)}</span>
          <span className={classNames('font-semibold', sdrGoalStatusClass[item.status])}>{progressLabel}</span>
        </div>
      </div>

      <div className="relative h-4 rounded-md bg-muted">
        <div
          className={classNames('h-full rounded-md transition-all', sdrGoalFillClass[item.status])}
          style={{ width: `${achievedWidth}%` }}
        />
        <div
          className="absolute top-[-3px] h-[calc(100%+6px)] w-[3px] rounded-full bg-textPrimary shadow-sm"
          style={{ left: `calc(${markerPosition}% - 1.5px)` }}
          role="img"
          aria-label={`Meta mensal: ${formatNumber(item.monthly_goal)}`}
        />
      </div>
    </div>
  );
}

function SdrGoalsCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
  manualReferenceMonth,
  onReferenceMonthChange,
}: {
  data: CommercialSdrGoalsResponse | null;
  errorMessage: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
  manualReferenceMonth: string | null;
  onReferenceMonthChange: (value: string | null) => void;
}) {
  const availableGoalTypes = useMemo<GoalType[]>(() => {
    const responseTypes = data?.available_goal_types ?? [];
    if (responseTypes.length > 0) return responseTypes;
    return Array.from(new Set((data?.items ?? []).map((item) => item.goal_type)));
  }, [data]);
  const [selectedGoalType, setSelectedGoalType] = useState<GoalType>('PASTAS_APROVADAS');

  useEffect(() => {
    if (availableGoalTypes.includes(selectedGoalType)) return;
    setSelectedGoalType(availableGoalTypes[0] ?? 'PASTAS_APROVADAS');
  }, [availableGoalTypes, selectedGoalType]);

  const items = (data?.items ?? []).filter((item) => item.goal_type === selectedGoalType);
  const referenceMonth = manualReferenceMonth ?? data?.reference_month ?? currentReferenceMonth();
  const monthOptions = useMemo(() => buildReferenceMonthOptions(referenceMonth), [referenceMonth]);
  const scaleMax = Math.max(...items.map((item) => Math.max(item.achieved, item.monthly_goal)), 1) * 1.1;
  const hasLoadError = errorMessage.trim() !== '';
  const asOfLabel = data?.as_of_date ? formatDateLabel(data.as_of_date) : 'a data atual';
  const selectedGoalLabel = sdrGoalTypeLabels[selectedGoalType];

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader
        title={`Acompanhamento de meta — ${selectedGoalLabel.toLocaleLowerCase('pt-BR')}`}
        description="Realizado (barra) vs. meta mensal (marcador) — ordenado por % de meta atingida"
        className="px-4 py-3"
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          {filterControl}
          {availableGoalTypes.length > 1 && (
            <HabitaSelect
              aria-label="Indicador da meta por SDR"
              className="h-9 min-w-[180px]"
              options={availableGoalTypes.map((type) => ({
                label: sdrGoalTypeLabels[type],
                value: type,
              }))}
              value={selectedGoalType}
              onChange={(event) => setSelectedGoalType(event.target.value as GoalType)}
            />
          )}
          {manualReferenceMonth && (
            <HabitaButton icon="ri-refresh-line" onClick={() => onReferenceMonthChange(null)} size="sm" variant="ghost">
              Usar período global
            </HabitaButton>
          )}
          <HabitaSelect
            className="h-9 rounded-md border border-border-subtle bg-surface px-3 font-instrument text-xs font-medium text-textPrimary outline-none transition-colors focus:border-primary"
            options={monthOptions.map((month) => ({
              label: formatMonthLabel(month),
              value: month,
            }))}
            value={referenceMonth}
            onChange={(event) => onReferenceMonthChange(event.target.value)}
            aria-label="Mês de referência"
          />
        </div>
      </HabitaSectionHeader>
      {filterSummary}

      <div className="space-y-3 p-4">
        {hasLoadError && (
          <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>
        )}

        {data?.note && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 font-instrument text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <i className="ri-information-line mt-0.5 shrink-0" aria-hidden />
            <span>{data.note} Mês exibido: {formatMonthLabel(data.reference_month)}. Realizado até {asOfLabel}.</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-56 animate-pulse rounded bg-muted" />
            <div className="h-28 animate-pulse rounded-lg bg-muted/60" />
          </div>
        ) : items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => (
              <SdrGoalRow key={`${item.goal_type}:${item.sdr}`} item={item} scaleMax={scaleMax} />
            ))}
          </div>
        ) : (
          <HabitaEmptyState
            icon="ri-focus-3-line"
            title={hasLoadError ? 'Não foi possível carregar metas' : 'Metas mensais não configuradas'}
            description={
              hasLoadError
                ? 'Revise a consulta do n8n para o recurso de metas dos SDRs.'
                : `Não há meta de ${selectedGoalLabel.toLocaleLowerCase('pt-BR')} cadastrada para o mês selecionado.`
            }
          />
        )}
      </div>
    </section>
  );
}

function SdrOpenWorkloadTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: CommercialSdrOpenWorkloadItem }>;
}) {
  const item = payload?.[0]?.payload;
  if (!active || !item) return null;

  const total = Math.max(item.total_portfolio, 0);

  return (
    <HabitaChartTooltipSurface className="min-w-72 p-3">
      <p className="font-instrument text-sm font-semibold text-textPrimary">{item.sdr}</p>
      <div className="mt-2 flex items-center justify-between gap-6 border-b border-border-subtle pb-2 font-instrument text-xs">
        <span className="text-textSecondary">Total da carteira</span>
        <span className="font-semibold text-textPrimary">{formatNumber(total)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {sdrOpenWorkloadGroups.map((group) => {
          const value = Math.max(item[group.key], 0);
          const share = total > 0 ? (value / total) * 100 : 0;

          return (
            <div key={group.key} className="flex items-center justify-between gap-6 font-instrument text-xs">
              <span className="flex items-center gap-1.5 text-textSecondary">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: group.color }}
                  aria-hidden
                />
                {group.label}
              </span>
              <span className="whitespace-nowrap font-semibold text-textPrimary">
                {formatNumber(value)} · {formatPercent(share)}
              </span>
            </div>
          );
        })}
      </div>
    </HabitaChartTooltipSurface>
  );
}

function SdrOpenWorkloadChart({ items }: { items: CommercialSdrOpenWorkloadItem[] }) {
  return (
    <div className="overflow-x-auto pb-1">
      <div
        className="h-80"
        style={{ width: `max(100%, ${Math.max(720, items.length * 104)}px)` }}
      >
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart
            data={items}
            margin={{ bottom: 50, left: 0, right: 12, top: 28 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" vertical={false} />
            <XAxis
              dataKey="sdr"
              interval={0}
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickFormatter={(value) => {
                const label = String(value);
                return label.length > 18 ? `${label.slice(0, 17)}…` : label;
              }}
              tickLine={false}
              angle={-20}
              textAnchor="end"
              height={64}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickLine={false}
              width={42}
            />
            <Tooltip
              allowEscapeViewBox={{ x: false, y: false }}
              content={<SdrOpenWorkloadTooltip />}
              cursor={{ fill: 'rgba(20, 70, 42, 0.05)' }}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
            />
            {sdrOpenWorkloadGroups.map((group, index) => (
              <Bar
                key={group.key}
                dataKey={group.key}
                name={group.label}
                fill={group.color}
                maxBarSize={56}
                radius={index === sdrOpenWorkloadGroups.length - 1 ? [4, 4, 0, 0] : undefined}
                stackId="portfolio"
              >
                <LabelList
                  {...chartBarLabelProps}
                  position="top"
                  valueAccessor={(entry) => {
                    const item = entry.payload as CommercialSdrOpenWorkloadItem;
                    const hasVisibleSegmentAbove = sdrOpenWorkloadGroups
                      .slice(index + 1)
                      .some((nextGroup) => item[nextGroup.key] > 0);
                    return hasVisibleSegmentAbove
                      ? ''
                      : formatCountBarLabel(item.total_portfolio);
                  }}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SdrOpenWorkloadCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
}: {
  data: CommercialSdrOpenWorkloadResponse | null;
  errorMessage: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
}) {
  const items = data?.items ?? [];
  const hasLoadError = errorMessage.trim() !== '';

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Carteira ativa por SDR" className="px-4 py-3">
        {filterControl}
      </HabitaSectionHeader>
      {filterSummary}

      <div className="space-y-3 p-4">
        <p className="font-instrument text-xs text-textSecondary">
          Oportunidades segmentadas pelas etapas priorizadas da carteira.
        </p>

        {hasLoadError && (
          <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>
        )}

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-48 animate-pulse rounded bg-muted" />
            <div className="h-44 animate-pulse rounded-lg bg-muted/60" />
          </div>
        ) : items.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-border-subtle pb-3 font-instrument text-xs text-textSecondary">
              {sdrOpenWorkloadGroups.map((group) => (
                <span key={group.key}>
                  <i
                    className="ri-checkbox-blank-circle-fill"
                    style={{ color: group.color }}
                    aria-hidden
                  />{' '}
                  {group.label}
                </span>
              ))}
            </div>
            <SdrOpenWorkloadChart items={items} />
          </>
        ) : (
          <HabitaEmptyState
            icon="ri-stack-line"
            title={hasLoadError ? 'Não foi possível carregar a carteira por SDR' : 'Sem oportunidades na carteira'}
            description={
              hasLoadError
                ? 'Revise a consulta do n8n para o recurso da carteira por SDR.'
                : 'Não há oportunidades nas etapas da carteira para os filtros selecionados.'
            }
          />
        )}
      </div>
    </section>
  );
}

function stageTimeCellStyle(avgDays: number | null, maxAvgDays: number): { color: string; backgroundColor: string } {
  if (avgDays === null || maxAvgDays <= 0) {
    return {
      backgroundColor: 'rgba(148, 163, 184, 0.12)',
      color: 'var(--color-text-secondary, #64748b)',
    };
  }

  const intensity = Math.min(1, Math.max(0, avgDays / maxAvgDays));
  const hue = 142 - (142 * intensity);
  const lightness = 92 - (34 * intensity);

  return {
    backgroundColor: `hsl(${hue} 68% ${lightness}%)`,
    color: intensity > 0.72 ? '#ffffff' : '#17462a',
  };
}

function SdrStageTimeCell({
  item,
  maxAvgDays,
}: {
  item: CommercialSdrStageTimeItem | undefined;
  maxAvgDays: number;
}) {
  if (!item || item.avg_days === null) {
    return (
      <td className="min-w-32 border-l border-border-subtle px-2 py-2 text-center font-instrument text-xs text-textTertiary">
        -
      </td>
    );
  }

  return (
    <td
      className="min-w-32 border-l border-border-subtle px-2 py-2 text-center font-instrument text-xs font-semibold"
      style={stageTimeCellStyle(item.avg_days, maxAvgDays)}
      title={[
        `${item.sdr} · ${item.stage_name}`,
        `Média: ${formatDurationDays(item.avg_days)}`,
        `Menor: ${formatDurationDays(item.min_days)}`,
        `Maior: ${formatDurationDays(item.max_days)}`,
        `Registros: ${formatNumber(item.lead_count)}`,
      ].join('\n')}
    >
      <span>{formatDurationDays(item.avg_days)}</span>
      <span className="ml-1 opacity-75">({formatNumber(item.lead_count)})</span>
    </td>
  );
}

function SdrStageTimeGridCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
}: {
  data: CommercialSdrStageTimeResponse | null;
  errorMessage?: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
}) {
  const stages = data?.stages ?? [];
  const sdrs = data?.sdrs ?? [];
  const itemsByKey = useMemo(() => {
    const entries = (data?.items ?? []).map((item) => [`${item.sdr}__${item.stage_index}`, item] as const);
    return new Map(entries);
  }, [data?.items]);
  const normalizedErrorMessage = errorMessage?.trim() ?? '';
  const hasLoadError = normalizedErrorMessage !== '';

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Tempo médio por etapa e SDR" className="px-4 py-3">
        {filterControl}
      </HabitaSectionHeader>
      {filterSummary}

      <div className="space-y-3 p-4">
        <p className="font-instrument text-xs text-textSecondary">
          Grade de permanência média por etapa do funil. Cores mais quentes indicam maior tempo relativo no período.
        </p>

        {hasLoadError && (
          <HabitaErrorNotice>{normalizedErrorMessage}</HabitaErrorNotice>
        )}

        {isLoading ? (
          <div className="space-y-2">
            <div className="h-4 w-56 animate-pulse rounded bg-muted" />
            <div className="h-44 animate-pulse rounded-lg bg-muted/60" />
          </div>
        ) : sdrs.length > 0 && stages.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle pb-3 font-instrument text-xs text-textSecondary">
              <span className="font-semibold text-textPrimary">Escala</span>
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-8 rounded-sm bg-emerald-100 ring-1 ring-border-subtle" aria-hidden />
                menor tempo
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-8 rounded-sm bg-red-500 ring-1 ring-border-subtle" aria-hidden />
                maior tempo
              </span>
              <span>Máximo da matriz: {formatDurationDays(data?.max_avg_days ?? null)}</span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-border-subtle">
              <table className="min-w-full border-collapse bg-surface text-left">
                <thead>
                  <tr className="border-b border-border-subtle bg-muted/30">
                    <th className="sticky left-0 z-10 min-w-44 bg-muted px-3 py-2 font-instrument text-xs font-semibold text-textSecondary">
                      SDR
                    </th>
                    {stages.map((stage) => (
                      <th
                        key={stage.stage_index}
                        className="min-w-36 border-l border-border-subtle px-2 py-2 text-center font-instrument text-[11px] font-semibold text-textSecondary"
                        title={stage.stage_name}
                      >
                        <span className="line-clamp-2">{stage.stage_name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sdrs.map((sdr) => (
                    <tr key={sdr} className="border-b border-border-subtle last:border-b-0">
                      <th className="sticky left-0 z-10 max-w-52 bg-surface px-3 py-2 font-instrument text-xs font-semibold text-textPrimary">
                        <span className="line-clamp-2">{sdr}</span>
                      </th>
                      {stages.map((stage) => (
                        <SdrStageTimeCell
                          key={`${sdr}-${stage.stage_index}`}
                          item={itemsByKey.get(`${sdr}__${stage.stage_index}`)}
                          maxAvgDays={data?.max_avg_days ?? 0}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <HabitaEmptyState
            icon="ri-grid-line"
            title={hasLoadError ? 'Não foi possível carregar tempos por etapa' : 'Sem histórico de permanência'}
            description={
              hasLoadError
                ? 'Revise a consulta do n8n para o recurso de tempo por etapa.'
                : 'Não há períodos de histórico de etapa para os filtros selecionados.'
            }
          />
        )}
      </div>
    </section>
  );
}

function SdrConversionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: CommercialSdrConversionItem }>;
}) {
  const item = payload?.[0]?.payload;
  if (!active || !item) return null;

  const rows = [
    {
      color: '#2e7bb4',
      count: item.generated_folders,
      label: 'CPF Limpo → Pasta',
      rate: item.mql_to_folder_rate_pct,
    },
    {
      color: '#047857',
      count: item.approved_leads,
      label: 'Aprovadas',
      rate: item.folder_to_approved_only_rate_pct,
    },
    {
      color: '#f4b942',
      count: item.conditional_leads,
      label: 'Aprovadas com condicional',
      rate: item.folder_to_conditional_rate_pct,
    },
    {
      color: '#dc3f45',
      count: item.rejected_leads,
      label: 'Reprovadas',
      rate: item.folder_to_rejected_rate_pct,
    },
  ];

  return (
    <HabitaChartTooltipSurface className="min-w-64 p-3">
      <p className="font-instrument text-sm font-semibold text-textPrimary">{item.sdr}</p>
      <div className="mt-2 flex items-center justify-between gap-6 border-b border-border-subtle pb-2 font-instrument text-xs">
        <span className="text-textSecondary">Leads com CPF Limpo</span>
        <span className="font-semibold text-textPrimary">{formatNumber(item.mql_leads)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-6 font-instrument text-xs">
            <span className="flex items-center gap-1.5 text-textSecondary">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
                aria-hidden
              />
              {row.label}
            </span>
            <span className="whitespace-nowrap font-semibold text-textPrimary">
              {formatNumber(row.count)} · {formatPercent(row.rate)}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 border-t border-border-subtle pt-2 font-instrument text-[11px] text-textSecondary">
        Desfechos calculados sobre {formatNumber(item.generated_folders)} pastas geradas.
      </p>
    </HabitaChartTooltipSurface>
  );
}

function SdrConversionsCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
}: {
  data: CommercialSdrConversionsResponse | null;
  errorMessage: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
}) {
  const items = data?.items ?? [];
  const hasLoadError = errorMessage.trim() !== '';

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader
        title="Conversão por SDR"
        className="px-4 py-3"
      >
        {filterControl}
      </HabitaSectionHeader>
      {filterSummary}

      <div className="space-y-3 p-4">
        <p className="font-instrument text-xs text-textSecondary">
          Compare as quantidades de pastas geradas e seus desfechos por SDR.
        </p>

        {hasLoadError && (
          <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>
        )}

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-52 animate-pulse rounded bg-muted" />
            <div className="h-52 animate-pulse rounded-lg bg-muted/60" />
          </div>
        ) : items.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-border-subtle pb-3 font-instrument text-xs text-textSecondary">
              <span>Total CPF Limpo: <strong className="text-textPrimary">{formatNumber(data?.totals.mql_leads ?? 0)}</strong></span>
              <span>Pastas geradas: <strong className="text-textPrimary">{formatNumber(data?.totals.generated_folders ?? 0)}</strong></span>
              <span>Aprovadas: <strong className="text-textPrimary">{formatNumber(data?.totals.approved_leads ?? 0)}</strong></span>
              <span>Condicional: <strong className="text-textPrimary">{formatNumber(data?.totals.conditional_leads ?? 0)}</strong></span>
              <span>Reprovadas: <strong className="text-textPrimary">{formatNumber(data?.totals.rejected_leads ?? 0)}</strong></span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 font-instrument text-[11px] text-textSecondary">
              <span><i className="ri-checkbox-blank-circle-fill text-[#2e7bb4]" aria-hidden /> CPF Limpo → Pasta</span>
              <span><i className="ri-checkbox-blank-circle-fill text-[#047857]" aria-hidden /> Aprovada</span>
              <span><i className="ri-checkbox-blank-circle-fill text-[#f4b942]" aria-hidden /> Aprovada com condicional</span>
              <span><i className="ri-checkbox-blank-circle-fill text-[#dc3f45]" aria-hidden /> Reprovada</span>
            </div>
            <div className="overflow-x-auto pb-1">
              <div
                className="h-80"
                style={{ width: `max(100%, ${Math.max(720, items.length * 112)}px)` }}
              >
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart
                    data={items}
                    margin={{ bottom: 50, left: 0, right: 12, top: 28 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" vertical={false} />
                    <XAxis
                      dataKey="sdr"
                      interval={0}
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      tickFormatter={(value) => {
                        const label = String(value);
                        return label.length > 18 ? `${label.slice(0, 17)}…` : label;
                      }}
                      tickLine={false}
                      angle={-20}
                      textAnchor="end"
                      height={64}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: 'currentColor', fontSize: 11 }}
                      tickLine={false}
                      width={42}
                    />
                    <Tooltip
                      allowEscapeViewBox={{ x: false, y: false }}
                      content={<SdrConversionTooltip />}
                      cursor={{ fill: 'rgba(20, 70, 42, 0.05)' }}
                      wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                    />
                    <Bar
                      dataKey="generated_folders"
                      name="CPF Limpo → Pasta"
                      fill="#2e7bb4"
                      maxBarSize={32}
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList
                        {...chartBarLabelProps}
                        formatter={formatCountBarLabel}
                        position="top"
                      />
                    </Bar>
                    <Bar
                      dataKey="approved_leads"
                      name="Aprovada"
                      fill="#047857"
                      maxBarSize={32}
                      stackId="outcomes"
                    >
                      <LabelList
                        {...chartBarLabelProps}
                        position="top"
                        valueAccessor={(entry) => {
                          const item = entry.payload as CommercialSdrConversionItem;
                          return item.conditional_leads > 0 || item.rejected_leads > 0
                            ? ''
                            : formatCountBarLabel(item.generated_folders);
                        }}
                      />
                    </Bar>
                    <Bar
                      dataKey="conditional_leads"
                      name="Aprovada com condicional"
                      fill="#f4b942"
                      maxBarSize={32}
                      stackId="outcomes"
                    >
                      <LabelList
                        {...chartBarLabelProps}
                        position="top"
                        valueAccessor={(entry) => {
                          const item = entry.payload as CommercialSdrConversionItem;
                          return item.rejected_leads > 0
                            ? ''
                            : formatCountBarLabel(item.generated_folders);
                        }}
                      />
                    </Bar>
                    <Bar
                      dataKey="rejected_leads"
                      name="Reprovada"
                      fill="#dc3f45"
                      maxBarSize={32}
                      radius={[4, 4, 0, 0]}
                      stackId="outcomes"
                    >
                      <LabelList
                        {...chartBarLabelProps}
                        position="top"
                        valueAccessor={(entry) => {
                          const item = entry.payload as CommercialSdrConversionItem;
                          return formatCountBarLabel(item.generated_folders);
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        ) : (
          <HabitaEmptyState
            icon="ri-bar-chart-grouped-line"
            title={hasLoadError ? 'Não foi possível carregar conversões' : 'Sem conversões por SDR'}
            description={
              hasLoadError
                ? 'Revise a consulta do n8n para o recurso de conversões dos SDRs.'
                : 'Não há SDRs com CPF Limpo ou pasta gerada no período e filtros selecionados.'
            }
          />
        )}
      </div>
    </section>
  );
}

interface FunnelEventsChartRow {
  date: string;
  label: string;
  [key: string]: string | number;
}

interface FunnelEventsTooltipPayload {
  color?: string;
  dataKey?: string | number;
  name?: string;
  value?: number | string;
}

function sumFunnelEventItems(
  items: CommercialFunnelEventItem[],
  predicate: (item: CommercialFunnelEventItem) => boolean
): number {
  return items.reduce((total, item) => total + (predicate(item) ? item.count : 0), 0);
}

function groupEventItemsByChannel(items: CommercialFunnelEventItem[]): Array<{ channel: string; count: number }> {
  const grouped = items.reduce((acc, item) => {
    acc.set(item.channel, (acc.get(item.channel) ?? 0) + item.count);
    return acc;
  }, new Map<string, number>());

  return Array.from(grouped.entries())
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => b.count - a.count || a.channel.localeCompare(b.channel));
}

function groupEventItemsBySdr(items: CommercialFunnelEventItem[]): Array<{ sdr: string; count: number }> {
  const grouped = items.reduce((acc, item) => {
    acc.set(item.sdr, (acc.get(item.sdr) ?? 0) + item.count);
    return acc;
  }, new Map<string, number>());

  return Array.from(grouped.entries())
    .map(([sdr, count]) => ({ sdr, count }))
    .sort((a, b) => b.count - a.count || a.sdr.localeCompare(b.sdr, 'pt-BR'));
}

function FunnelEventsTooltip({
  active,
  items,
  mode,
  payload,
  sdrMetric,
  sdrNamesByKey,
}: {
  active?: boolean;
  items: CommercialFunnelEventItem[];
  mode: 'sdr' | 'team';
  payload?: FunnelEventsTooltipPayload[];
  sdrMetric: CommercialFunnelEventKey;
  sdrNamesByKey: Record<string, string>;
}) {
  if (!active || !payload?.length) return null;

  const date = String((payload[0] as { payload?: FunnelEventsChartRow }).payload?.date ?? '');
  const dayLabel = formatDateLabel(date);
  const visiblePayload = payload.filter((entry) => Number(entry.value) > 0);
  const total = visiblePayload.reduce((sum, entry) => sum + Number(entry.value ?? 0), 0);

  return (
    <HabitaChartTooltipSurface className="min-w-56 p-3">
      <p className="font-instrument text-sm font-semibold text-textPrimary">{dayLabel}</p>
      <p className="mt-1 font-instrument text-xs text-textSecondary">Total do dia: {formatNumber(total)}</p>

      <div className="mt-3 space-y-3">
        {visiblePayload.map((entry) => {
          const key = String(entry.dataKey ?? '');
          const eventKey = mode === 'team' ? key as CommercialFunnelEventKey : sdrMetric;
          const sdr = mode === 'sdr' ? sdrNamesByKey[key] ?? key : null;
          const eventItems = items.filter((item) => (
            item.date === date
            && item.event_key === eventKey
            && (!sdr || item.sdr === sdr)
          ));
          const showApprovalBySdr = mode === 'team' && eventKey === 'approval';
          const channelBreakdown = showApprovalBySdr ? [] : groupEventItemsByChannel(eventItems);
          const sdrBreakdown = showApprovalBySdr ? groupEventItemsBySdr(eventItems) : [];

          return (
            <div key={`${key}-${eventKey}`} className="space-y-1">
              <div className="flex items-center justify-between gap-3 font-instrument text-xs">
                <span className="font-semibold text-textPrimary">
                  <span
                    className="mr-1 inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: entry.color }}
                    aria-hidden
                  />
                  {entry.name ?? key}
                </span>
                <span className="text-textPrimary">{formatNumber(Number(entry.value ?? 0))}</span>
              </div>
              {sdrBreakdown.length > 0 && (
                <div className="max-h-36 space-y-0.5 overflow-y-auto pl-3 font-instrument text-[11px] text-textSecondary">
                  <p className="mb-1 font-semibold text-textPrimary">Pastas aprovadas por SDR</p>
                  {sdrBreakdown.map((item) => (
                    <div key={item.sdr} className="flex justify-between gap-4">
                      <span className="truncate">{item.sdr}</span>
                      <span>{formatNumber(item.count)}</span>
                    </div>
                  ))}
                </div>
              )}
              {channelBreakdown.length > 0 && (
                <div className="space-y-0.5 pl-3 font-instrument text-[11px] text-textSecondary">
                  <p className="mb-1 font-semibold text-textPrimary">Por canal</p>
                  {channelBreakdown.slice(0, 5).map((item) => (
                    <div key={item.channel} className="flex justify-between gap-4">
                      <span className="truncate">{item.channel}</span>
                      <span>{formatNumber(item.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </HabitaChartTooltipSurface>
  );
}

function FunnelEventLegendButton({
  active,
  color,
  label,
  onClick,
}: {
  active: boolean;
  color: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={classNames(
        'inline-flex h-8 items-center gap-2 rounded-md border px-2.5 font-instrument text-xs font-medium transition-colors',
        active
          ? 'border-primary/30 bg-primary/10 text-textPrimary'
          : 'border-border-subtle bg-surface text-textSecondary hover:bg-accent hover:text-textPrimary'
      )}
    >
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      {label}
    </button>
  );
}

function FunnelEventsTrendCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
  selectedSdrs,
}: {
  data: CommercialFunnelEventsResponse | null;
  errorMessage: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
  selectedSdrs: string[];
}) {
  const items = data?.items ?? [];
  const days = data?.days ?? [];
  const isSdrMode = selectedSdrs.length > 0;
  const [activeTeamEvents, setActiveTeamEvents] = useState<CommercialFunnelEventKey[]>([
    'folder_generated',
    'approval',
    'rejection',
  ]);
  const [activeSdrEvent, setActiveSdrEvent] = useState<CommercialFunnelEventKey>('folder_generated');
  const [chartType, setChartType] = useState<CommercialTrendChartType>('bar');
  const activeEvents = activeTeamEvents.length > 0 ? activeTeamEvents : ['folder_generated'];
  const sdrSeries = useMemo(() => selectedSdrs.map((sdr, index) => ({
    color: sdrLineColors[index % sdrLineColors.length],
    dataKey: `sdr_${index}`,
    name: sdr,
  })), [selectedSdrs]);
  const visibleBarSeriesCount = isSdrMode ? sdrSeries.length : activeEvents.length;
  const showFunnelEventBarLabels = shouldDisplayInlineBarLabels(
    days.length,
    visibleBarSeriesCount
  );
  const sdrNamesByKey = useMemo(() => Object.fromEntries(
    sdrSeries.map((series) => [series.dataKey, series.name])
  ), [sdrSeries]);

  const teamRows = useMemo<FunnelEventsChartRow[]>(() => days.map((day) => ({
    date: day,
    label: formatDateLabel(day).slice(0, 5),
    approval: sumFunnelEventItems(items, (item) => item.date === day && item.event_key === 'approval'),
    folder_generated: sumFunnelEventItems(items, (item) => item.date === day && item.event_key === 'folder_generated'),
    rejection: sumFunnelEventItems(items, (item) => item.date === day && item.event_key === 'rejection'),
  })), [days, items]);

  const sdrRows = useMemo<FunnelEventsChartRow[]>(() => days.map((day) => {
    const row: FunnelEventsChartRow = {
      date: day,
      label: formatDateLabel(day).slice(0, 5),
    };

    sdrSeries.forEach((series) => {
      row[series.dataKey] = sumFunnelEventItems(items, (item) => (
        item.date === day
        && item.event_key === activeSdrEvent
        && item.sdr === series.name
      ));
    });

    return row;
  }), [activeSdrEvent, days, items, sdrSeries]);

  function toggleTeamEvent(key: CommercialFunnelEventKey) {
    setActiveTeamEvents((current) => {
      if (current.includes(key)) {
        const next = current.filter((item) => item !== key);
        return next.length > 0 ? next : current;
      }
      return [...current, key];
    });
  }

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader
        title="Ritmo diário do funil"
        className="px-4 py-3"
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          {filterControl}
          <TrendChartTypeToggle
            label="Visualização do ritmo diário do funil"
            value={chartType}
            onChange={setChartType}
          />
        </div>
      </HabitaSectionHeader>
      {filterSummary}

      <div className="space-y-4 p-4">
        <p className="font-instrument text-xs text-textSecondary">
          Volume diário absoluto de pastas geradas, aprovações e reprovações.
        </p>

        {errorMessage.trim() && (
          <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {commercialFunnelEvents.map((event) => (
              <FunnelEventLegendButton
                key={event.key}
                active={isSdrMode ? activeSdrEvent === event.key : activeEvents.includes(event.key)}
                color={event.color}
                label={event.label}
                onClick={() => {
                  if (isSdrMode) {
                    setActiveSdrEvent(event.key);
                  } else {
                    toggleTeamEvent(event.key);
                  }
                }}
              />
            ))}
          </div>

          {isSdrMode && selectedSdrs.length >= 3 && (
            <p className="rounded-md bg-amber-50 px-2.5 py-1 font-instrument text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
              {selectedSdrs.length} SDRs selecionados. A comparação pode ficar mais carregada.
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            <div className="h-80 animate-pulse rounded-lg bg-muted/60" />
          </div>
        ) : items.length > 0 && days.length > 0 ? (
          <div className="h-80 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <ComposedChart
                data={isSdrMode ? sdrRows : teamRows}
                margin={{ bottom: 8, left: 0, right: 12, top: showFunnelEventBarLabels ? 28 : 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" vertical={false} />
                <XAxis
                  dataKey="label"
                  minTickGap={18}
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  tickLine={false}
                  width={42}
                />
                <Tooltip
                  allowEscapeViewBox={{ x: false, y: false }}
                  content={(
                    <FunnelEventsTooltip
                      items={items}
                      mode={isSdrMode ? 'sdr' : 'team'}
                      sdrMetric={activeSdrEvent}
                      sdrNamesByKey={sdrNamesByKey}
                    />
                  )}
                  wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                />
                {chartType === 'bar'
                  ? (
                    isSdrMode
                      ? sdrSeries.map((series) => (
                        <Bar
                          key={series.dataKey}
                          dataKey={series.dataKey}
                          name={series.name}
                          fill={series.color}
                          maxBarSize={28}
                          radius={[3, 3, 0, 0]}
                        >
                          {showFunnelEventBarLabels && (
                            <LabelList
                              {...chartBarLabelProps}
                              formatter={formatCountBarLabel}
                              position="top"
                            />
                          )}
                        </Bar>
                      ))
                      : commercialFunnelEvents
                        .filter((event) => activeEvents.includes(event.key))
                        .map((event) => (
                          <Bar
                            key={event.key}
                            dataKey={event.key}
                            name={event.label}
                            fill={event.color}
                            maxBarSize={28}
                            radius={[3, 3, 0, 0]}
                          >
                            {showFunnelEventBarLabels && (
                              <LabelList
                                {...chartBarLabelProps}
                                formatter={formatCountBarLabel}
                                position="top"
                              />
                            )}
                          </Bar>
                        ))
                  )
                  : (
                    isSdrMode
                      ? sdrSeries.map((series) => (
                        <Line
                          key={series.dataKey}
                          type="monotone"
                          dataKey={series.dataKey}
                          name={series.name}
                          stroke={series.color}
                          strokeWidth={2.2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      ))
                      : commercialFunnelEvents
                        .filter((event) => activeEvents.includes(event.key))
                        .map((event) => (
                          <Line
                            key={event.key}
                            type="monotone"
                            dataKey={event.key}
                            name={event.label}
                            stroke={event.color}
                            strokeWidth={2.2}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                        ))
                  )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <HabitaEmptyState
            icon="ri-line-chart-line"
            title="Sem eventos no período"
            description="Não há eventos de pasta gerada, aprovação ou reprovação para os filtros selecionados."
          />
        )}
      </div>
    </section>
  );
}

function SdrOutcomeTimeCard({
  data,
  errorMessage,
  filterControl,
  filterSummary,
  isLoading,
}: {
  data: CommercialSdrOutcomeTimeResponse | null;
  errorMessage: string;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
}) {
  const items = data?.items ?? [];
  const hasChartData = items.some((item) => (
    item.approval_median_days !== null ||
    item.disqualification_median_days !== null
  ));
  const showOutcomeTimeBarLabels = shouldDisplayInlineBarLabels(items.length, 2);

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader
        title="Tempo até o desfecho"
        className="px-4 py-3"
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="font-instrument text-xs font-normal text-textSecondary">Mediana em dias por SDR</span>
          {filterControl}
        </div>
      </HabitaSectionHeader>
      {filterSummary}
      <div className="space-y-3 p-4">
        {errorMessage && <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>}
        {isLoading ? (
          <div className="h-80 animate-pulse rounded-lg bg-muted/60" />
        ) : hasChartData ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart
                data={items}
                margin={{ bottom: 8, left: 0, right: 12, top: showOutcomeTimeBarLabels ? 28 : 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 70, 42, 0.12)" />
                <XAxis dataKey="sdr_name" tick={{ fill: 'currentColor', fontSize: 11 }} />
                <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} unit="d" />
                <Tooltip
                  allowEscapeViewBox={{ x: false, y: false }}
                  content={<HabitaRechartsTooltip valueFormatter={(value) => `${formatDecimal(Number(value), 1)}d`} />}
                  wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
                />
                <Bar dataKey="approval_median_days" name="Até aprovação" fill="#2f8f5b" radius={[4, 4, 0, 0]}>
                  {showOutcomeTimeBarLabels && (
                    <LabelList {...chartBarLabelProps} formatter={formatDaysBarLabel} position="top" />
                  )}
                </Bar>
                <Bar dataKey="disqualification_median_days" name="Até desqualificação" fill="#6b7280" radius={[4, 4, 0, 0]}>
                  {showOutcomeTimeBarLabels && (
                    <LabelList {...chartBarLabelProps} formatter={formatDaysBarLabel} position="top" />
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <HabitaEmptyState
            icon="ri-timer-line"
            title="Sem desfechos suficientes"
            description="Não há aprovações ou desqualificações com tempo calculável para os filtros atuais."
          />
        )}
      </div>
    </section>
  );
}

function PerformanceTab({
  funnelEvents,
  funnelEventsError,
  funnelEventsLoading,
  sdrStageTime,
  sdrStageTimeError,
  sdrStageTimeLoading,
  sdrOutcomeTime,
  sdrOutcomeTimeError,
  sdrOutcomeTimeLoading,
  sdrConversions,
  sdrConversionsError,
  sdrConversionsLoading,
  sdrOpenWorkload,
  sdrOpenWorkloadError,
  sdrOpenWorkloadLoading,
  selectedSdrs,
  renderFilterControl,
  renderFilterSummary,
  manualReferenceMonth,
  onReferenceMonthChange,
  sdrGoalsError,
  sdrGoals,
  sdrGoalsLoading,
}: {
  funnelEvents: CommercialFunnelEventsResponse | null;
  funnelEventsError: string;
  funnelEventsLoading: boolean;
  sdrStageTime: CommercialSdrStageTimeResponse | null;
  sdrStageTimeError: string;
  sdrStageTimeLoading: boolean;
  sdrOutcomeTime: CommercialSdrOutcomeTimeResponse | null;
  sdrOutcomeTimeError: string;
  sdrOutcomeTimeLoading: boolean;
  sdrConversions: CommercialSdrConversionsResponse | null;
  sdrConversionsError: string;
  sdrConversionsLoading: boolean;
  sdrOpenWorkload: CommercialSdrOpenWorkloadResponse | null;
  sdrOpenWorkloadError: string;
  sdrOpenWorkloadLoading: boolean;
  selectedSdrs: string[];
  renderFilterControl: (widgetKey: CommercialWidgetKey) => ReactNode;
  renderFilterSummary: (widgetKey: CommercialWidgetKey) => ReactNode;
  manualReferenceMonth: string | null;
  onReferenceMonthChange: (value: string | null) => void;
  sdrGoalsError: string;
  sdrGoals: CommercialSdrGoalsResponse | null;
  sdrGoalsLoading: boolean;
}) {
  return (
    <div className="space-y-5">
      <SdrGoalsCard
        data={sdrGoals}
        errorMessage={sdrGoalsError}
        filterControl={renderFilterControl('performance-goals')}
        filterSummary={renderFilterSummary('performance-goals')}
        isLoading={sdrGoalsLoading}
        manualReferenceMonth={manualReferenceMonth}
        onReferenceMonthChange={onReferenceMonthChange}
      />
      <SdrConversionsCard
        data={sdrConversions}
        errorMessage={sdrConversionsError}
        filterControl={renderFilterControl('performance-conversions')}
        filterSummary={renderFilterSummary('performance-conversions')}
        isLoading={sdrConversionsLoading}
      />
      <FunnelEventsTrendCard
        data={funnelEvents}
        errorMessage={funnelEventsError}
        filterControl={renderFilterControl('performance-funnel-events')}
        filterSummary={renderFilterSummary('performance-funnel-events')}
        isLoading={funnelEventsLoading}
        selectedSdrs={selectedSdrs}
      />
      <SdrStageTimeGridCard
        data={sdrStageTime}
        errorMessage={sdrStageTimeError}
        filterControl={renderFilterControl('performance-stage-time')}
        filterSummary={renderFilterSummary('performance-stage-time')}
        isLoading={sdrStageTimeLoading}
      />
      <SdrOpenWorkloadCard
        data={sdrOpenWorkload}
        errorMessage={sdrOpenWorkloadError}
        filterControl={renderFilterControl('performance-open-workload')}
        filterSummary={renderFilterSummary('performance-open-workload')}
        isLoading={sdrOpenWorkloadLoading}
      />
      <SdrOutcomeTimeCard
        data={sdrOutcomeTime}
        errorMessage={sdrOutcomeTimeError}
        filterControl={renderFilterControl('performance-outcome-time')}
        filterSummary={renderFilterSummary('performance-outcome-time')}
        isLoading={sdrOutcomeTimeLoading}
      />
    </div>
  );
}

export default function Comercial() {
  const location = useLocation();
  const navigate = useNavigate();
  const repository = useMemo(() => new CommercialRepository(), []);
  const goalsRepository = useMemo(() => new SdrGoalsRepository(), []);
  const performanceRankingTabRef = useRef<PerformanceRankingTabHandle>(null);
  const authProfiles = useMemo(() => getAuthProfiles(), []);
  const canViewAllSdrs = useMemo(
    () => authProfiles.some((profile) => (
      ['ADMIN', 'GERENTE', 'GESTOR', 'SUPERVISOR'].includes(profile)
    )),
    [authProfiles]
  );
  const isSdrScopedUser = authProfiles.includes('SDR') && !canViewAllSdrs;
  const canViewPerformanceRanking = useMemo(
    () => canViewAllSdrs,
    [canViewAllSdrs]
  );
  const visibleCommercialTabs = useMemo(
    () => commercialTabs.filter((tab) => tab.key !== 'ranking' || canViewPerformanceRanking),
    [canViewPerformanceRanking]
  );
  const [activeTab, setActiveTab] = useState<CommercialTab>('overview');
  const [dateFilter, setDateFilter] = useState<HabitaDateRangeFilterValue>(readInitialDateFilter);
  const [dateBasis, setDateBasis] = useState<CommercialDateBasis>(readInitialDateBasis);
  const [filters, setFilters] = useState<CommercialFilterState>(createDefaultFilters);
  const [draftFilters, setDraftFilters] = useState<CommercialFilterState>(createDefaultFilters);
  const [widgetFilterOverrides, setWidgetFilterOverrides] = useState<
    Partial<Record<CommercialWidgetKey, CommercialWidgetFilterOverride>>
  >({});
  const [filterOptions, setFilterOptions] = useState<CommercialFilterOptionsResponse>(emptyOptions);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [activeWidgetFilterKey, setActiveWidgetFilterKey] = useState<CommercialWidgetKey | null>(null);
  const [funnel, setFunnel] = useState<CommercialFunnelResponse | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelEvents, setFunnelEvents] = useState<CommercialFunnelEventsResponse | null>(null);
  const [performanceRanking, setPerformanceRanking] = useState<CommercialSdrRankingResponse | null>(null);
  const [performanceRankingIndicator, setPerformanceRankingIndicator] =
    useState<CommercialSdrRankingIndicator>('approved_folders');
  const [manualReferenceMonth, setManualReferenceMonth] = useState<string | null>(null);
  const [overview, setOverview] = useState<CommercialOverviewResponse | null>(null);
  const [goalConfiguration, setGoalConfiguration] = useState<GoalsCompetencyConfiguration | null>(null);
  const [goalProgress, setGoalProgress] = useState<CommercialTeamGoalProgressResponse | null>(null);
  const [sdrGoals, setSdrGoals] = useState<CommercialSdrGoalsResponse | null>(null);
  const [sdrConversions, setSdrConversions] = useState<CommercialSdrConversionsResponse | null>(null);
  const [sdrOpenWorkload, setSdrOpenWorkload] = useState<CommercialSdrOpenWorkloadResponse | null>(null);
  const [sdrStageTime, setSdrStageTime] = useState<CommercialSdrStageTimeResponse | null>(null);
  const [interestRegions, setInterestRegions] = useState<CommercialInterestRegionsResponse | null>(null);
  const [operationMetricsByWidget, setOperationMetricsByWidget] = useState<
    Partial<Record<OperationWidgetKey, CommercialOperationMetricsResponse | null>>
  >({});
  const [channelsQualityByWidget, setChannelsQualityByWidget] = useState<
    Partial<Record<ChannelWidgetKey, CommercialChannelsQualityResponse | null>>
  >({});
  const [sdrOutcomeTime, setSdrOutcomeTime] = useState<CommercialSdrOutcomeTimeResponse | null>(null);
  const [funnelEventsError, setFunnelEventsError] = useState('');
  const [funnelEventsLoading, setFunnelEventsLoading] = useState(false);
  const [performanceRankingError, setPerformanceRankingError] = useState('');
  const [performanceRankingLoading, setPerformanceRankingLoading] = useState(false);
  const [sdrGoalsError, setSdrGoalsError] = useState('');
  const [sdrGoalsLoading, setSdrGoalsLoading] = useState(false);
  const [sdrConversionsError, setSdrConversionsError] = useState('');
  const [sdrConversionsLoading, setSdrConversionsLoading] = useState(false);
  const [sdrOpenWorkloadError, setSdrOpenWorkloadError] = useState('');
  const [sdrOpenWorkloadLoading, setSdrOpenWorkloadLoading] = useState(false);
  const [sdrStageTimeError, setSdrStageTimeError] = useState('');
  const [operationMetricsError, setOperationMetricsError] = useState('');
  const [channelsQualityError, setChannelsQualityError] = useState('');
  const [sdrOutcomeTimeError, setSdrOutcomeTimeError] = useState('');
  const [sdrOutcomeTimeLoading, setSdrOutcomeTimeLoading] = useState(false);
  const [channelsQualityLoading, setChannelsQualityLoading] = useState(false);
  const [operationMetricsLoading, setOperationMetricsLoading] = useState(false);
  const [interestRegionsLoading, setInterestRegionsLoading] = useState(false);
  const [sdrStageTimeLoading, setSdrStageTimeLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [goalTrackingError, setGoalTrackingError] = useState('');
  const [goalTrackingLoading, setGoalTrackingLoading] = useState(true);
  const resolvedDateRange = useMemo(() => resolveHabitaDateRange(dateFilter), [dateFilter]);
  const maxDateInput = useMemo(() => formatHabitaDateInput(new Date()), []);

  const filterGroups = useMemo<CommercialFilterGroup[]>(() => {
    const groups: CommercialFilterGroup[] = [
      { key: 'channel', label: 'Canal de entrada', options: filterOptions.channels },
      { key: 'channelDetail', label: 'Detalhamento do canal de entrada', options: filterOptions.details },
      {
        collapsibleOptionGroup: {
          group: 'other',
          label: 'Outros',
        },
        key: 'contactDdd',
        label: 'DDD do contato',
        options: filterOptions.contact_ddds,
      },
      {
        key: 'interestCity',
        label: 'Cidade de interesse',
        options: filterOptions.interest_cities,
      },
      {
        key: 'opportunityStatus',
        label: 'Status da oportunidade',
        options: filterOptions.opportunity_statuses.length > 0
          ? filterOptions.opportunity_statuses
          : opportunityStatusOptions,
      },
      { key: 'stage', label: 'Etapa do funil SDR', options: filterOptions.stages },
      { key: 'cpfStatus', label: 'Status CPF', options: filterOptions.cpf_statuses },
      { key: 'sdr', label: 'SDR responsável', options: filterOptions.sdrs },
    ];

    return isSdrScopedUser
      ? groups.filter((group) => group.key !== 'sdr')
      : groups;
  }, [filterOptions, isSdrScopedUser]);

  const queryParams = useMemo<CommercialFetchParams>(() => (
    buildCommercialQueryParams(
      filters,
      dateBasis,
      resolvedDateRange.startDate,
      resolvedDateRange.endDate
    )
  ), [dateBasis, filters, resolvedDateRange.endDate, resolvedDateRange.startDate]);
  const performanceRankingQueryParams = useMemo<CommercialFetchParams>(() => (
    {
      ...buildCommercialQueryParams(
        resolveWidgetFilters(
          'performance-conversions',
          filters,
          widgetFilterOverrides['performance-conversions']
        ),
        dateBasis,
        resolvedDateRange.startDate,
        resolvedDateRange.endDate
      ),
      ranking_indicator: performanceRankingIndicator,
    }
  ), [
    dateBasis,
    filters,
    performanceRankingIndicator,
    resolvedDateRange.endDate,
    resolvedDateRange.startDate,
    widgetFilterOverrides,
  ]);
  const performanceRankingCopy = commercialRankingIndicatorCopy[performanceRankingIndicator];
  const currentCompetency = useMemo(() => currentReferenceMonth(), []);
  const goalProgressQueryParams = useMemo<CommercialFetchParams>(() => ({
    ...buildCommercialQueryParams(filters, 'stage', undefined, undefined),
    referenceMonth: currentCompetency,
  }), [currentCompetency, filters]);

  const widgetFiltersByKey = useMemo<Record<CommercialWidgetKey, CommercialFilterState>>(() => (
    Object.fromEntries(commercialWidgetKeys.map((widgetKey) => [
      widgetKey,
      resolveWidgetFilters(widgetKey, filters, widgetFilterOverrides[widgetKey]),
    ])) as Record<CommercialWidgetKey, CommercialFilterState>
  ), [filters, widgetFilterOverrides]);

  const widgetQueryParams = useMemo<Record<CommercialWidgetKey, CommercialFetchParams>>(() => (
    Object.fromEntries(commercialWidgetKeys.map((widgetKey) => [
      widgetKey,
      buildCommercialQueryParams(
        widgetFiltersByKey[widgetKey],
        dateBasis,
        resolvedDateRange.startDate,
        resolvedDateRange.endDate
      ),
    ])) as Record<CommercialWidgetKey, CommercialFetchParams>
  ), [dateBasis, resolvedDateRange.endDate, resolvedDateRange.startDate, widgetFiltersByKey]);

  const selectedFilters = useMemo(() => activeFilterItems(filters, filterGroups), [filterGroups, filters]);
  const selectedFilterCount = selectedFilters.length;
  const activeWidgetFilters = activeWidgetFilterKey ? widgetFiltersByKey[activeWidgetFilterKey] : null;
  const activeWidgetDefaults = activeWidgetFilterKey ? createWidgetDefaultFilters(activeWidgetFilterKey) : null;
  const activeWidgetOverride = activeWidgetFilterKey
    ? widgetFilterOverrides[activeWidgetFilterKey]
    : undefined;
  const redirectToLogin = useCallback(() => {
    const requestedPath = `${location.pathname}${location.search}${location.hash}`;
    navigate({
      pathname: '/login',
      search: `?redirect=${encodeURIComponent(requestedPath)}`,
    }, {
      replace: true,
      state: { from: requestedPath },
    });
  }, [location.hash, location.pathname, location.search, navigate]);

  const loadOverview = useCallback(async (refreshing = false) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setErrorMessage('');
    if (refreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setFunnelLoading(true);

    const overviewRequest = repository.fetchOverview(queryParams, accessToken)
      .then((response) => {
        setOverview(response);
        setErrorMessage('');
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage('Não foi possível carregar o painel comercial.');
      })
      .finally(() => setIsLoading(false));

    const funnelRequest = repository.fetchFunnel(queryParams, accessToken)
      .then((response) => setFunnel(response))
      .catch((error) => {
        console.error(error);
        setFunnel(null);
      })
      .finally(() => setFunnelLoading(false));

    await Promise.allSettled([overviewRequest, funnelRequest]);
    setIsRefreshing(false);
  }, [queryParams, redirectToLogin, repository]);

  const loadGoalTracking = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setGoalTrackingLoading(true);
    setGoalTrackingError('');

    const [configurationResult, progressResult] = await Promise.allSettled([
      goalsRepository.fetchCompetency(currentCompetency),
      repository.fetchTeamGoalProgress(goalProgressQueryParams, accessToken),
    ]);

    if (configurationResult.status === 'fulfilled') {
      setGoalConfiguration(configurationResult.value);
    } else {
      console.error(configurationResult.reason);
      setGoalConfiguration(null);
    }

    if (
      progressResult.status === 'fulfilled'
      && progressResult.value.reference_month === currentCompetency
    ) {
      setGoalProgress(progressResult.value);
    } else {
      console.error(
        progressResult.status === 'rejected'
          ? progressResult.reason
          : new Error('A API comercial retornou uma competência diferente da solicitada.')
      );
      setGoalProgress(null);
    }

    if (configurationResult.status === 'rejected') {
      setGoalTrackingError('Não foi possível carregar a configuração da Meta do Time para a competência atual.');
    } else if (
      progressResult.status === 'rejected'
      || progressResult.value.reference_month !== currentCompetency
    ) {
      setGoalTrackingError('Não foi possível carregar o realizado da Meta do Time para a competência atual.');
    }

    setGoalTrackingLoading(false);
  }, [
    currentCompetency,
    goalProgressQueryParams,
    goalsRepository,
    redirectToLogin,
    repository,
  ]);

  const loadChannelsQuality = useCallback(async (refreshing = false) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setChannelsQualityLoading(true);
    if (refreshing) setIsRefreshing(true);

    try {
      const result = await fetchDeduplicatedWidgetData(
        channelWidgetKeys,
        widgetQueryParams,
        (params) => repository.fetchChannelsQuality(params, accessToken)
      );
      result.errors.forEach((error) => console.error(error));
      setChannelsQualityByWidget(result.responses);
      setChannelsQualityError(result.failedWidgetKeys.length > 0
        ? 'Não foi possível atualizar todos os componentes de canais e qualidade.'
        : '');
    } catch (error) {
      console.error(error);
      setChannelsQualityError('Não foi possível carregar canais e qualidade.');
    } finally {
      setChannelsQualityLoading(false);
      if (refreshing) setIsRefreshing(false);
    }
  }, [redirectToLogin, repository, widgetQueryParams]);

  const loadOperationMetrics = useCallback(async (refreshing = false) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setOperationMetricsLoading(true);
    setInterestRegionsLoading(true);
    if (refreshing) setIsRefreshing(true);

    try {
      const [metricsResult, regionsResult] = await Promise.allSettled([
        fetchDeduplicatedWidgetData(
          operationMetricsWidgetKeys,
          widgetQueryParams,
          (params) => repository.fetchOperationMetrics(params, accessToken)
        ),
        repository.fetchInterestRegions(
          widgetQueryParams['operation-interest-regions'],
          accessToken
        ),
      ]);

      let hasFailure = false;

      if (metricsResult.status === 'fulfilled') {
        metricsResult.value.errors.forEach((error) => console.error(error));
        setOperationMetricsByWidget(metricsResult.value.responses);
        hasFailure = metricsResult.value.failedWidgetKeys.length > 0;
      } else {
        console.error(metricsResult.reason);
        hasFailure = true;
      }

      if (regionsResult.status === 'fulfilled') {
        setInterestRegions(regionsResult.value);
      } else {
        console.error(regionsResult.reason);
        setInterestRegions(null);
        hasFailure = true;
      }

      setOperationMetricsError(hasFailure
        ? 'Não foi possível atualizar todos os componentes de operação.'
        : '');
    } catch (error) {
      console.error(error);
      setOperationMetricsError('Não foi possível carregar as métricas operacionais.');
    } finally {
      setOperationMetricsLoading(false);
      setInterestRegionsLoading(false);
      if (refreshing) setIsRefreshing(false);
    }
  }, [redirectToLogin, repository, widgetQueryParams]);

  const loadPerformanceData = useCallback(async (refreshing = false) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setSdrGoalsLoading(true);
    setSdrConversionsLoading(true);
    setSdrOpenWorkloadLoading(true);
    setFunnelEventsLoading(true);
    setSdrStageTimeLoading(true);
    setSdrOutcomeTimeLoading(true);
    if (refreshing) setIsRefreshing(true);

    const requests = [
      repository.fetchSdrGoals({
        ...widgetQueryParams['performance-goals'],
        referenceMonth: manualReferenceMonth ?? undefined,
      }, accessToken)
        .then((response) => {
          setSdrGoals(response);
          setSdrGoalsError('');
        })
        .catch((error) => {
          console.error(error);
          setSdrGoals(null);
          setSdrGoalsError('Não foi possível carregar o acompanhamento de metas dos SDRs.');
        })
        .finally(() => setSdrGoalsLoading(false)),
      repository.fetchSdrConversions(widgetQueryParams['performance-conversions'], accessToken)
        .then((response) => {
          setSdrConversions(response);
          setSdrConversionsError('');
        })
        .catch((error) => {
          console.error(error);
          setSdrConversions(null);
          setSdrConversionsError('Não foi possível carregar as conversões por SDR.');
        })
        .finally(() => setSdrConversionsLoading(false)),
      repository.fetchSdrOpenWorkload(widgetQueryParams['performance-open-workload'], accessToken)
        .then((response) => {
          setSdrOpenWorkload(response);
          setSdrOpenWorkloadError('');
        })
        .catch((error) => {
          console.error(error);
          setSdrOpenWorkload(null);
          setSdrOpenWorkloadError('Não foi possível carregar a carga atual por SDR.');
        })
        .finally(() => setSdrOpenWorkloadLoading(false)),
      repository.fetchFunnelEvents(widgetQueryParams['performance-funnel-events'], accessToken)
        .then((response) => {
          setFunnelEvents(response);
          setFunnelEventsError('');
        })
        .catch((error) => {
          console.error(error);
          setFunnelEvents(null);
          setFunnelEventsError('Não foi possível carregar o ritmo diário do funil.');
        })
        .finally(() => setFunnelEventsLoading(false)),
      repository.fetchSdrStageTime(widgetQueryParams['performance-stage-time'], accessToken)
        .then((response) => {
          setSdrStageTime(response);
          setSdrStageTimeError('');
        })
        .catch((error) => {
          console.error(error);
          setSdrStageTime(null);
          setSdrStageTimeError('Não foi possível carregar os tempos por etapa e SDR.');
        })
        .finally(() => setSdrStageTimeLoading(false)),
      repository.fetchSdrOutcomeTime(widgetQueryParams['performance-outcome-time'], accessToken)
        .then((response) => {
          setSdrOutcomeTime(response);
          setSdrOutcomeTimeError('');
        })
        .catch((error) => {
          console.error(error);
          setSdrOutcomeTime(null);
          setSdrOutcomeTimeError('Não foi possível carregar o tempo até desfecho.');
        })
        .finally(() => setSdrOutcomeTimeLoading(false)),
    ];

    await Promise.allSettled(requests);
    if (refreshing) setIsRefreshing(false);
  }, [manualReferenceMonth, redirectToLogin, repository, widgetQueryParams]);

  const loadPerformanceRanking = useCallback(async (refreshing = false) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    setPerformanceRankingLoading(true);
    setPerformanceRankingError('');
    if (refreshing) setIsRefreshing(true);

    try {
      const response = await repository.fetchSdrRanking(performanceRankingQueryParams, accessToken);
      setPerformanceRanking(response);
      setPerformanceRankingError('');
    } catch (error) {
      console.error(error);
      setPerformanceRanking(null);
      setPerformanceRankingError(
        `Não foi possível carregar o ranking de ${performanceRankingCopy.label.toLocaleLowerCase('pt-BR')}.`
      );
    } finally {
      setPerformanceRankingLoading(false);
      if (refreshing) setIsRefreshing(false);
    }
  }, [performanceRankingCopy.label, performanceRankingQueryParams, redirectToLogin, repository]);

  const loadFilterOptions = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      redirectToLogin();
      return;
    }

    try {
      const options = await repository.fetchFilterOptions({
        date_basis: dateBasis,
        endDate: resolvedDateRange.endDate,
        opportunity_status: opportunityStatusOptions.map((option) => option.value).join(','),
        startDate: resolvedDateRange.startDate,
      }, accessToken);
      setFilterOptions(options);
    } catch (error) {
      console.error(error);
      setFilterOptions(emptyOptions);
    }
  }, [dateBasis, redirectToLogin, repository, resolvedDateRange.endDate, resolvedDateRange.startDate]);

  useEffect(() => {
    if (activeTab === 'ranking' && !canViewPerformanceRanking) {
      setActiveTab('overview');
      return;
    }

    if (activeTab === 'overview') {
      void loadOverview();
      return;
    }

    if (activeTab === 'channels') {
      void loadChannelsQuality();
      return;
    }

    if (activeTab === 'operation') {
      void loadOperationMetrics();
      return;
    }

    if (activeTab === 'ranking') {
      void loadPerformanceRanking();
      return;
    }

    void loadPerformanceData();
  }, [
    activeTab,
    canViewPerformanceRanking,
    loadChannelsQuality,
    loadOperationMetrics,
    loadOverview,
    loadPerformanceData,
    loadPerformanceRanking,
  ]);

  useEffect(() => {
    if (activeTab === 'overview') void loadGoalTracking();
  }, [activeTab, loadGoalTracking]);

  useEffect(() => {
    void loadFilterOptions();
  }, [loadFilterOptions]);

  function applyDateFilter(nextDateFilter: HabitaDateRangeFilterValue) {
    setDateFilter(nextDateFilter);
    persistDateFilter(nextDateFilter);
  }

  function clearDateFilter() {
    const nextDateFilter = defaultDateFilter();
    setDateFilter(nextDateFilter);
    persistDateFilter(nextDateFilter);
  }

  function changeDateBasis(nextDateBasis: CommercialDateBasis) {
    setDateBasis(nextDateBasis);
    persistDateBasis(nextDateBasis);
  }

  function openFilterDialog() {
    setDraftFilters(cloneFilters(filters));
    setIsFilterDialogOpen(true);
  }

  function toggleDraftFilter(key: CommercialFilterKey, value: string) {
    setDraftFilters((current) => {
      const values = current[key];
      const nextValues = values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value];

      return {
        ...current,
        [key]: nextValues,
      };
    });
  }

  function applyFilters() {
    const nextFilters = cloneFilters(draftFilters);
    const addedFilters = commercialFilterKeys.reduce<CommercialFilterState>((added, key) => {
      added[key] = nextFilters[key].filter((value) => !filters[key].includes(value));
      return added;
    }, createEmptyFilters());

    if (!areFiltersEqual(filters, nextFilters)) {
      setFilters(nextFilters);
      setWidgetFilterOverrides((current) => {
        let changed = false;
        const next = { ...current };

        commercialWidgetKeys.forEach((widgetKey) => {
          const override = current[widgetKey];
          if (!override) return;
          const nextOverride: CommercialWidgetFilterOverride = {
            added: cloneFilters(override.added),
            removed: cloneFilters(override.removed),
          };
          let overrideChanged = false;

          commercialFilterKeys.forEach((key) => {
            if (addedFilters[key].length === 0) return;
            const addedValues = new Set(addedFilters[key]);
            const nextAddedValues = nextOverride.added[key].filter((item) => !addedValues.has(item));
            const nextRemovedValues = nextOverride.removed[key].filter((item) => !addedValues.has(item));
            if (
              nextAddedValues.length === nextOverride.added[key].length
              && nextRemovedValues.length === nextOverride.removed[key].length
            ) {
              return;
            }

            overrideChanged = true;
            nextOverride.added[key] = nextAddedValues;
            nextOverride.removed[key] = nextRemovedValues;
          });

          if (overrideChanged) {
            changed = true;
            next[widgetKey] = nextOverride;
          }
        });

        return changed ? next : current;
      });
    }

    setIsFilterDialogOpen(false);
  }

  function clearFilters() {
    setFilters(createEmptyFilters());
  }

  function clearFilterValue(key: CommercialFilterKey, value: string) {
    setFilters((current) => ({
      ...current,
      [key]: current[key].filter((item) => item !== value),
    }));
  }

  function toggleWidgetFilter(
    widgetKey: CommercialWidgetKey,
    key: CommercialFilterKey,
    value: string
  ) {
    setWidgetFilterOverrides((current) => {
      const currentOverride = current[widgetKey] ?? createEmptyWidgetFilterOverride();
      const effectiveFilters = resolveWidgetFilters(widgetKey, filters, currentOverride);
      const defaults = createWidgetDefaultFilters(widgetKey);
      const isSelected = effectiveFilters[key].includes(value);
      const isInherited = defaults[key].includes(value) || filters[key].includes(value);
      const addedValues = currentOverride.added[key].filter((item) => item !== value);
      const removedValues = currentOverride.removed[key].filter((item) => item !== value);

      if (isSelected && isInherited) {
        removedValues.push(value);
      } else if (!isSelected && !isInherited) {
        addedValues.push(value);
      }

      return {
        ...current,
        [widgetKey]: {
          added: {
            ...currentOverride.added,
            [key]: addedValues,
          },
          removed: {
            ...currentOverride.removed,
            [key]: removedValues,
          },
        },
      };
    });
  }

  function resetWidgetFilters(widgetKey: CommercialWidgetKey) {
    setWidgetFilterOverrides((current) => {
      if (!current[widgetKey]) return current;
      const next = { ...current };
      delete next[widgetKey];
      return next;
    });
  }

  function renderWidgetFilterControl(widgetKey: CommercialWidgetKey) {
    const filterCount = activeFilterItems(widgetFiltersByKey[widgetKey], filterGroups).length;
    return (
      <CommercialWidgetFilterButton
        filterCount={filterCount}
        isCustomized={hasWidgetFilterOverride(widgetFilterOverrides[widgetKey])}
        onClick={() => setActiveWidgetFilterKey(widgetKey)}
      />
    );
  }

  function renderWidgetFilterSummary(widgetKey: CommercialWidgetKey) {
    return (
      <CommercialWidgetFilterSummary
        filters={widgetFiltersByKey[widgetKey]}
        groups={filterGroups}
        onRemove={(key, value) => toggleWidgetFilter(widgetKey, key, value)}
      />
    );
  }

  function refreshActiveTab() {
    void loadFilterOptions();

    if (activeTab === 'overview') {
      void loadOverview(true);
      void loadGoalTracking();
      return;
    }

    if (activeTab === 'channels') {
      void loadChannelsQuality(true);
      return;
    }

    if (activeTab === 'operation') {
      void loadOperationMetrics(true);
      return;
    }

    if (activeTab === 'ranking') {
      void loadPerformanceRanking(true);
      return;
    }

    void loadPerformanceData(true);
  }

  function renderActiveTab() {
    if (activeTab === 'overview') {
      return (
        <OverviewTab
          data={overview}
          funnel={funnel}
          funnelLoading={funnelLoading}
          goalConfiguration={goalConfiguration}
          goalProgress={goalProgress}
          goalTrackingError={goalTrackingError}
          goalTrackingLoading={goalTrackingLoading}
          onDetails={() => setActiveTab('channels')}
        />
      );
    }
    if (activeTab === 'channels') {
      if (channelsQualityLoading && Object.keys(channelsQualityByWidget).length === 0) {
        return <CommercialTabLoadingState title="Carregando canais e qualidade" />;
      }

      return (
        <ChannelsQualityTab
          dataByWidget={channelsQualityByWidget}
          errorMessage={channelsQualityError}
          renderFilterControl={renderWidgetFilterControl}
          renderFilterSummary={renderWidgetFilterSummary}
        />
      );
    }
    if (activeTab === 'operation') {
      if (operationMetricsLoading && Object.keys(operationMetricsByWidget).length === 0) {
        return <CommercialTabLoadingState title="Carregando operação e funil" />;
      }

      return (
        <OperationFunnelTab
          interestRegions={interestRegions}
          interestRegionsLoading={interestRegionsLoading}
          operationMetricsByWidget={operationMetricsByWidget}
          operationMetricsError={operationMetricsError}
          renderFilterControl={renderWidgetFilterControl}
          renderFilterSummary={renderWidgetFilterSummary}
        />
      );
    }
    if (activeTab === 'ranking') {
      return (
        <PerformanceRankingTab
          ref={performanceRankingTabRef}
          data={performanceRanking}
          error={performanceRankingError}
          indicator={performanceRankingIndicator}
          onRefresh={() => void loadPerformanceRanking(true)}
          periodLoading={
            performanceRankingLoading
            || (!performanceRanking && !performanceRankingError)
          }
        />
      );
    }
    return (
      <PerformanceTab
        funnelEvents={funnelEvents}
        funnelEventsError={funnelEventsError}
        funnelEventsLoading={funnelEventsLoading}
        manualReferenceMonth={manualReferenceMonth}
        onReferenceMonthChange={setManualReferenceMonth}
        renderFilterControl={renderWidgetFilterControl}
        renderFilterSummary={renderWidgetFilterSummary}
        selectedSdrs={widgetFiltersByKey['performance-funnel-events'].sdr}
        sdrStageTime={sdrStageTime}
        sdrStageTimeError={sdrStageTimeError}
        sdrStageTimeLoading={sdrStageTimeLoading}
        sdrOutcomeTime={sdrOutcomeTime}
        sdrOutcomeTimeError={sdrOutcomeTimeError}
        sdrOutcomeTimeLoading={sdrOutcomeTimeLoading}
        sdrConversions={sdrConversions}
        sdrConversionsError={sdrConversionsError}
        sdrConversionsLoading={sdrConversionsLoading}
        sdrGoalsError={sdrGoalsError}
        sdrGoals={sdrGoals}
        sdrGoalsLoading={sdrGoalsLoading}
        sdrOpenWorkload={sdrOpenWorkload}
        sdrOpenWorkloadError={sdrOpenWorkloadError}
        sdrOpenWorkloadLoading={sdrOpenWorkloadLoading}
      />
    );
  }

  return (
    <main className="w-full space-y-5 p-4 sm:p-6">
      <header>
        <h1 className="font-instrument text-2xl font-bold text-textPrimary">
          {activeTab === 'ranking' ? performanceRankingCopy.pageTitle : 'Comercial'}
        </h1>
        <p className="mt-1 font-instrument text-sm text-textSecondary">
          {activeTab === 'ranking'
            ? performanceRankingCopy.pageDescription
            : 'Acompanhamento do funil de crédito imobiliário integrado ao CRM.'}
        </p>
      </header>

      <section className={classNames(habitaSurfaceClass, 'p-3')}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0 overflow-x-auto">
            <div className="flex min-w-max items-center gap-1">
              {visibleCommercialTabs.map((tab) => (
                <CommercialTabButton
                  key={tab.key}
                  active={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </CommercialTabButton>
              ))}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <HabitaDateRangeFilter
              dateBasis={dateBasis}
              maxDateInput={maxDateInput}
              onApply={applyDateFilter}
              onClear={clearDateFilter}
              onDateBasisChange={changeDateBasis}
              showDateBasis
              value={dateFilter}
            />
            <HabitaButton
              icon="ri-filter-3-line"
              onClick={openFilterDialog}
              size="sm"
              variant={selectedFilterCount > 0 ? 'primary' : 'secondary'}
            >
              Filtros
              {selectedFilterCount > 0 && (
                <span className="ml-0.5 rounded-full bg-black/10 px-1.5 text-[11px] dark:bg-white/20">
                  {selectedFilterCount}
                </span>
              )}
            </HabitaButton>
            <HabitaButton
              disabled={isRefreshing}
              icon="ri-refresh-line"
              onClick={refreshActiveTab}
              size="sm"
            >
              Atualizar
            </HabitaButton>
            {activeTab === 'ranking' && (
              <label className="flex items-center gap-2">
                <span className="font-instrument text-xs font-semibold text-textSecondary">
                  Indicador
                </span>
                <HabitaSelect
                  aria-label="Indicador do ranking"
                  disabled={performanceRankingLoading}
                  onChange={(event) => {
                    const nextIndicator = event.target.value as CommercialSdrRankingIndicator;
                    if (nextIndicator === performanceRankingIndicator) return;
                    setPerformanceRanking(null);
                    setPerformanceRankingError('');
                    setPerformanceRankingIndicator(nextIndicator);
                  }}
                  options={commercialRankingIndicatorOptions}
                  value={performanceRankingIndicator}
                />
              </label>
            )}
            {activeTab === 'ranking' && (
              <HabitaButton
                icon="ri-tv-2-line"
                onClick={() => performanceRankingTabRef.current?.enterTvMode()}
                size="sm"
              >
                Visão TV
              </HabitaButton>
            )}
          </div>
        </div>

        {selectedFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedFilters.map((filter) => (
              <HabitaActiveFilterChip
                key={`${filter.key}-${filter.value}`}
                label={filter.label}
                onRemove={() => clearFilterValue(filter.key, filter.value)}
                value={filter.displayValue}
              />
            ))}
            <HabitaButton icon="ri-close-circle-line" onClick={clearFilters} size="sm" variant="ghost">
              Limpar filtros
            </HabitaButton>
          </div>
        )}
      </section>

      {errorMessage && <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>}

      {activeTab === 'overview' && isLoading ? (
        <section className={classNames(habitaSurfaceClass, 'p-6')}>
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {[0, 1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="h-36 animate-pulse rounded-lg bg-muted/70" />
            ))}
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-64 animate-pulse rounded-lg bg-muted/70" />
            ))}
          </div>
        </section>
      ) : (
        renderActiveTab()
      )}

      <HabitaDialog
        isOpen={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        title="Filtros"
        description={isSdrScopedUser
          ? 'Refine seus leads por canal, DDD, cidade de interesse, status, etapa e qualidade de CPF.'
          : 'Refine o Funil SDR por canal, DDD, cidade de interesse, status, etapa, qualidade de CPF e responsável.'}
        size="lg"
        footer={(
          <div className="flex w-full justify-between gap-2">
            <HabitaButton
              icon="ri-refresh-line"
              onClick={() => setDraftFilters(createEmptyFilters())}
              variant="ghost"
            >
              Limpar filtros
            </HabitaButton>
            <HabitaButton icon="ri-check-line" onClick={applyFilters}>
              Aplicar
            </HabitaButton>
          </div>
        )}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {filterGroups.map((group) => (
            <CommercialFilterGroupCard
              key={group.key}
              group={group}
              onToggle={(value) => toggleDraftFilter(group.key, value)}
              selectedValues={draftFilters[group.key]}
            />
          ))}
        </div>
      </HabitaDialog>

      <HabitaDialog
        isOpen={activeWidgetFilterKey !== null}
        onClose={() => setActiveWidgetFilterKey(null)}
        title={activeWidgetFilterKey
          ? `Filtros · ${commercialWidgetDefinitions[activeWidgetFilterKey].label}`
          : 'Filtros do componente'}
        description="Os filtros globais e os padrões deste componente já estão aplicados. Marque ou desmarque opções para ajustar somente esta visualização."
        size="lg"
        footer={(
          <div className="flex w-full flex-wrap justify-between gap-2">
            <HabitaButton
              disabled={!activeWidgetFilterKey || !hasWidgetFilterOverride(activeWidgetOverride)}
              icon="ri-restart-line"
              onClick={() => {
                if (activeWidgetFilterKey) resetWidgetFilters(activeWidgetFilterKey);
              }}
              variant="ghost"
            >
              Restaurar padrão
            </HabitaButton>
            <HabitaButton icon="ri-check-line" onClick={() => setActiveWidgetFilterKey(null)}>
              Concluir
            </HabitaButton>
          </div>
        )}
      >
        {activeWidgetFilterKey && activeWidgetFilters && activeWidgetDefaults && (
          <div className="space-y-4">
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 font-instrument text-xs text-textSecondary">
              Alterações feitas aqui não afetam os demais componentes. Uma nova seleção no filtro global
              volta a ser incluída em todas as visualizações da aba.
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {filterGroups.map((group) => (
                <CommercialFilterGroupCard
                  key={group.key}
                  group={group}
                  onToggle={(value) => (
                    toggleWidgetFilter(activeWidgetFilterKey, group.key, value)
                  )}
                  renderOptionMeta={(option) => {
                    const isGlobal = filters[group.key].includes(option.value);
                    const isDefault = activeWidgetDefaults[group.key].includes(option.value);
                    const isLocallyAdded = activeWidgetOverride?.added[group.key].includes(option.value) ?? false;
                    const isLocallyRemoved = activeWidgetOverride?.removed[group.key].includes(option.value) ?? false;

                    return (
                      <span className="flex shrink-0 flex-wrap justify-end gap-1">
                        {isGlobal && (
                          <span className="rounded bg-blue-500/10 px-1.5 py-0.5 font-instrument text-[10px] font-semibold text-blue-700 dark:text-blue-300">
                            Global
                          </span>
                        )}
                        {isDefault && (
                          <span className="rounded bg-primary/10 px-1.5 py-0.5 font-instrument text-[10px] font-semibold text-primary">
                            Padrão
                          </span>
                        )}
                        {isLocallyAdded && (
                          <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 font-instrument text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            Local
                          </span>
                        )}
                        {isLocallyRemoved && (
                          <span className="rounded bg-red-500/10 px-1.5 py-0.5 font-instrument text-[10px] font-semibold text-red-700 dark:text-red-300">
                            Removido aqui
                          </span>
                        )}
                      </span>
                    );
                  }}
                  selectedValues={activeWidgetFilters[group.key]}
                />
              ))}
            </div>
          </div>
        )}
      </HabitaDialog>
    </main>
  );
}
